import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  increment,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  query,
  where,
  writeBatch,
  orderBy,
  limit,
  runTransaction,
  startAfter,
  type DocumentReference,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { ExclusiveContentSubscriptionStatus } from '@/data/enums/exclusiveSubscriptions/exclusiveContentSubscriptionStatus';
import type { SavedContentDto } from '@/data/dtos/savedContent/savedContentDto';
import type { UserDeviceDto } from '@/data/dtos/userDevice/userDeviceDto';
import type { UserSettingDto } from '@/data/dtos/userSetting/userSettingDto';
import type { JsonObject, PageCursor } from '@/data/types/json';
import { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import { AppTransactionType } from '@/domain/enums/transaction/AppTransactionType';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * FirestoreService
 * 
 * Service layer for direct Firestore operations.
 * Used for reading data that doesn't require cloud function processing.
 * Write operations should generally go through CloudFunctionsService,
 * except for internal operations like analytics logging.
 * 
 * Note: This keeps the architecture clean by centralizing all Firestore
 * access in one place, matching Flutter's repository pattern.
 */
export class FirestoreService {
  private static instance: FirestoreService;

  public static getInstance(): FirestoreService {
    if (!FirestoreService.instance) {
      FirestoreService.instance = new FirestoreService();
    }
    return FirestoreService.instance;
  }

  /**
   * Get user settings from Firestore
   * Path: /users/{userId}/userSettings
   */
  async getUserSettings(userId: string): Promise<JsonObject[]> {
    try {
      const settingsRef = collection(db, 'users', userId, 'userSettings');
      const settingsSnapshot = await getDocs(settingsRef);

      const settings: JsonObject[] = [];
      settingsSnapshot.forEach((doc) => {
        settings.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return settings;
    } catch (error) {
      appLogger.error('Error fetching user settings', 'FirestoreService.getUserSettings', { error: error as Error });
      throw error;
    }
  }

  /**
   * Read the global default settings catalogue.
   * Path: /settings — a ROOT collection, fetched whole (no where / orderBy / limit).
   *
   * Mirrors Flutter's UserSettingsDataSource.getDefaultSettings
   * (lib/data/data_sources/user_settings_data_source.dart:34-51).
   *
   * Errors are swallowed (logged + returns []) because Flutter swallows them the
   * same way (:48-50) and the caller treats an empty list as "nothing to sync".
   * This deliberately diverges from its sibling `getUserSettings` above, which
   * rethrows.
   *
   * Flutter additionally bails with [] when there is no signed-in Firebase user
   * (:36-39); on web that check belongs to the caller (the use case), which only
   * runs this while authenticated.
   *
   * Authorization: cloud_functions/firestore.rules:11-13 grants read/write to any
   * authenticated user. `/settings` is global, read-only-in-practice configuration
   * (it is not admin-scoped data), so no server-side gate is required here and
   * Flutter has none either.
   *
   * Returns raw JsonObject documents; mapping to `UserSettingDto` is the domain
   * remapper's job.
   */
  async getDefaultSettings(): Promise<JsonObject[]> {
    try {
      const settingsRef = collection(db, 'settings');
      const settingsSnapshot = await getDocs(settingsRef);

      const settings: JsonObject[] = [];
      settingsSnapshot.forEach((doc) => {
        settings.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return settings;
    } catch (error) {
      appLogger.error('Error fetching default settings', 'FirestoreService.getDefaultSettings', { error: error as Error });
      return [];
    }
  }

  /**
   * Replace the user's settings subcollection with the supplied set:
   * delete every existing document under /users/{userId}/userSettings, then write
   * one document per entry at /users/{userId}/userSettings/{setting.name}.
   *
   * Mirrors Flutter's UserSettingsDataSource.syncUserSettings
   * (lib/data/data_sources/user_settings_data_source.dart:125-154) plus the
   * private `_clearUserSettings` it calls first (:156-180). The clear-then-write
   * ordering is preserved exactly. Two separate batches are used because a
   * `delete` and a `set` on the same document reference inside a single batch
   * would cancel out; Flutter's two `commit()` calls have the same effect.
   *
   * Flutter comment contradiction (mirrored, not "improved"): the comment at
   * :139-141 claims the `merge: true` "avoids overriding a setting value set by
   * the user", but `_clearUserSettings()` at :129 has already deleted those
   * documents, so the merge is a no-op and user-set values ARE reset to the
   * defaults. The code — not the comment — is the contract: a deliberate
   * reset-to-defaults on a settings-version bump. `merge: true` is kept for
   * literal parity.
   *
   * Each `settings` entry is the opaque document body the caller assembles; the
   * `toSettingUpdateInfo()` mapping (lib/data/models/user_setting.dart:60-72 —
   * name, title, description, category, defaultValue, value (`value ?? defaultValue`,
   * never null), premiumPackage, literals, dataType) lives in the domain remapper.
   * `setting.name` is the document id.
   *
   * DELIBERATE DIVERGENCE — behaviour change that fixes a Flutter bug:
   * an empty `settings` list is a no-op here and returns false. Flutter has no
   * such guard and its empty-list path destroys data:
   * sync_repository.dart:577 calls `syncUserSettings(userSettings)`
   * unconditionally (including with []); `syncUserSettings` runs
   * `await _clearUserSettings()` BEFORE the write batch
   * (user_settings_data_source.dart:129); and `_clearUserSettings` deletes EVERY
   * document under users/{uid}/userSettings (:156-180). With an empty list the
   * write batch is vacuous but the clear is not — Flutter wipes the
   * subcollection, commits an empty batch and still returns true (:153), so
   * `saved == true` at sync_repository.dart:578. The empty list is reachable in
   * both clients: `getDefaultSettings()` returns [] on a read error or a
   * signed-out caller (user_settings_data_source.dart:36-39,48-50) and the web
   * `getDefaultSettings` above carries the same swallow-and-return-[] contract.
   * The Flutter source is not patched here — a follow-up Flutter fix is
   * recommended.
   *
   * Authorization: cloud_functions/firestore.rules:11-13 grants read/write to any
   * authenticated user; this is the user's own subcollection (not admin-scoped
   * data), so both clients write it from the client and no server-side gate is
   * required.
   *
   * @returns true when both batches commit; false when `settings` is empty or on
   *          any Firestore failure (matching Flutter's `false` return, :149-151).
   */
  async replaceUserSettings(userId: string, settings: JsonObject[]): Promise<boolean> {
    if (settings.length === 0) {
      return false;
    }

    try {
      // 1. Clear every existing document — Flutter's _clearUserSettings (:163-176).
      const settingsRef = collection(db, 'users', userId, 'userSettings');
      const existingSnapshot = await getDocs(settingsRef);

      const deleteBatch = writeBatch(db);
      existingSnapshot.forEach((existingDoc) => {
        deleteBatch.delete(existingDoc.ref);
      });
      await deleteBatch.commit();

      // 2. Write the new set — Flutter's syncUserSettings write batch (:131-148).
      const writeBatchRef = writeBatch(db);
      for (const setting of settings) {
        const settingRef = doc(db, 'users', userId, 'userSettings', String(setting.name));
        writeBatchRef.set(settingRef, setting, { merge: true });
      }
      await writeBatchRef.commit();

      return true;
    } catch (error) {
      appLogger.error('Error replacing user settings', 'FirestoreService.replaceUserSettings', { context: { userId }, error: error as Error });
      return false;
    }
  }

  /**
   * Returns true if the target creator requires manual approval for new subscribers.
   * Path: /users/{targetUserId}/userSettings/approve_subscribers — true iff document exists
   * AND its UserSettingDto.value === 1.
   * Mirrors Flutter's UsersDataSource.shouldSendSubscriptionRequest
   * (lib/data/data_sources/users_data_source.dart:388-409). Errors are swallowed
   * (logged + returns false) to match Flutter's behavior.
   */
  async shouldSendSubscriptionRequest(targetUserId: string): Promise<boolean> {
    try {
      const docRef = doc(db, 'users', targetUserId, 'userSettings', 'approve_subscribers');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const dto = docSnap.data() as UserSettingDto;
        return dto.value === 1;
      }

      return false;
    } catch (error) {
      appLogger.error('Error checking shouldSendSubscriptionRequest', 'FirestoreService.shouldSendSubscriptionRequest', { context: { targetUserId }, error: error as Error });
      return false;
    }
  }

  /**
   * Write the current user's device record so the app can enforce a single
   * active session per user. Mirrors Flutter's
   * UserDeviceDataSource.setDeviceInfo
   * (lib/data/data_sources/user_device_data_source.dart:5-38) — but the web
   * port intentionally diverges in two ways:
   *
   * 1. Web has no push token yet, so we only write `{ userId, deviceId }`.
   *    `merge: true` ensures we don't clobber a `pushToken` that mobile may
   *    have written to the same `user_device/{userId}` document.
   * 2. Flutter wraps two writes (`users.pushToken` + `user_device`) in a
   *    transaction. We skip both the secondary `users.pushToken` write and
   *    the transaction — there's only one document to update.
   *
   * Path: /user_device/{userId} (matches Flutter's
   * `collection('user_device').doc(firebaseId)`).
   *
   * Field names (`userId`, `deviceId`) match Flutter exactly — the Dart
   * `UserDevice` model has no `@JsonKey` overrides, so property names are
   * the literal Firestore field names.
   *
   * Errors are rethrown so the caller's use case can decide how to react
   * (e.g. swallow on startup so we don't block app boot).
   */
  async setUserDevice(userId: string, deviceId: string): Promise<void> {
    try {
      const userDeviceRef = doc(db, 'user_device', userId);
      await setDoc(
        userDeviceRef,
        { userId, deviceId } satisfies Pick<UserDeviceDto, 'userId' | 'deviceId'>,
        { merge: true },
      );
    } catch (error) {
      appLogger.error('Error writing user_device', 'FirestoreService.setUserDevice', { context: { userId }, error: error as Error });
      throw error;
    }
  }

  /**
   * One-shot read of `user_device/{userId}`. Used by the startup takeover
   * check (`CheckUserDeviceTakeoverUseCase`) to detect that another browser
   * claimed the session while this one was closed — a case the live
   * `modified`-only subscription cannot catch.
   *
   * Returns `null` when the document does not exist (no device has ever
   * registered for this user). Errors propagate so the caller decides
   * whether to fail-open (do not sign out) or fail-closed.
   */
  async getUserDevice(userId: string): Promise<JsonObject | null> {
    const userDeviceRef = doc(db, 'user_device', userId);
    const snapshot = await getDoc(userDeviceRef);
    if (!snapshot.exists()) return null;
    return snapshot.data();
  }

  /**
   * Get user document from Firestore
   * Path: /users/{userId}
   */
  async getUser(userId: string): Promise<JsonObject | null> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        return {
          id: userDoc.id,
          ...userDoc.data()
        };
      }

      return null;
    } catch (error) {
      appLogger.error('Error fetching user', 'FirestoreService.getUser', { error: error as Error });
      throw error;
    }
  }

  async getUserMedia(mediaId: string): Promise<JsonObject | null> {
    try {
      const mediaRef = collectionGroup(db, 'media');
      const q = query(
        mediaRef,
        where('id', '==', mediaId),
        orderBy('created_timestamp', 'desc'),
        limit(1),
      );
      const snapshot = await getDocs(q);

      if (snapshot.docs.length > 0) {
        return {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data(),
        };
      }

      return null;
    } catch (error) {
      appLogger.error('Error fetching user media', 'FirestoreService.getUserMedia', { error: error as Error });
      throw error;
    }
  }

  /**
   * Mirrors Flutter's ContentDataSource.getMediaByPath
   * (lib/data/data_sources/content_data_source.dart:44).
   */
  async getUserMediaByPath(path: string): Promise<JsonObject | null> {
    try {
      const mediaRef = collectionGroup(db, 'media');
      const q = query(
        mediaRef,
        where('path', '==', path),
        orderBy('created_timestamp', 'desc'),
        limit(1),
      );
      const snapshot = await getDocs(q);

      if (snapshot.docs.length > 0) {
        return {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data(),
        };
      }

      return null;
    } catch (error) {
      appLogger.error('Error fetching user media by path', 'FirestoreService.getUserMediaByPath', { context: { path }, error: error as Error });
      throw error;
    }
  }

  /**
   * Mirrors Flutter's ContentDataSource.getUnlockedContent
   * (lib/data/data_sources/content_data_source.dart:362).
   *
   * Fetches media documents whose `path` matches any value in `paths` via a
   * `collectionGroup('media')` query, ordered by `created_timestamp` desc.
   *
   * Note: Firestore's `in` operator supports at most
   * `SERVER_FETCH_CONSTANTS.FIRESTORE_WHERE_IN_MAX_VALUES` values. The caller
   * is responsible for chunking upstream if needed; over-sized inputs throw.
   * Blocked / hidden / usersBlockedMe filtering is intentionally NOT performed
   * here — that belongs in the domain use case.
   */
  async getMediasByPaths(paths: string[]): Promise<JsonObject[]> {
    try {
      const mediaRef = collectionGroup(db, 'media');
      const q = query(
        mediaRef,
        where('path', 'in', paths),
        orderBy('created_timestamp', 'desc'),
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      appLogger.error('Error fetching user medias by paths', 'FirestoreService.getMediasByPaths', { context: { paths }, error: error as Error });
      throw error;
    }
  }

  /**
   * Get user's medias with pagination
   * Path: users/{userId}/media collection
   * 
   * Matches Flutter's getUserMedias from content_data_source.dart
   * 
   * @param userId - Firebase user ID to fetch medias for
   * @param currentUserId - Current authenticated user ID (for filtering)
   * @param cursor - Pagination cursor (opaque snapshot of last fetched item)
   * @param limitCount - Number of items to fetch
   * @returns Paginated user media items
   */
  async getUserMedias(
    userId: string,
    currentUserId: string,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      // Use collection to query user's media subcollection
      const mediaRef = collection(db, 'users', userId, 'media');

      let q = query(
        mediaRef,
        orderBy('created_timestamp', 'desc'),
        limit(limitCount)
      );

      // If not current user, filter out drafts, processing, and blocked content
      if (currentUserId !== userId) {
        q = query(q, where('isDraft', '==', false));
        q = query(q, where('isProcessing', '==', false));
        q = query(q, where('blocked', '==', false));
      }

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      let items: JsonObject[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching user medias', 'FirestoreService.getUserMedias', { error: error as Error });
      // Allow hasMore so that Retry can be attempted
      // Additionally, return cursor to avoid refetching same page
      return { items: [], hasMore: true, cursor: cursor };
    }
  }

  /**
   * Get the current user's medias that are eligible to be linked to another
   * piece of content. Matches Flutter's ContentDataSource.getMediasToLink
   * (lib/data/data_sources/content_data_source.dart).
   *
   * Path: users/{userId}/media
   * Filters: isDraft == false, isProcessing == false, blocked == false, contentLink == null
   * Order: created_timestamp desc
   * Pagination: cursor-based via startAfter(cursor)
   *
   * The Flutter implementation also excludes the draft's own mediaId client-side
   * (after the query) — mirroring that behaviour here.
   * `hasMore` is derived from the raw snapshot size (before client-side exclusion)
   * to match Flutter.
   */
  async getMediasToLink(
    userId: string,
    excludeMediaId: string,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const mediaRef = collection(db, 'users', userId, 'media');

      let q = query(
        mediaRef,
        where('isDraft', '==', false),
        where('isProcessing', '==', false),
        where('blocked', '==', false),
        where('contentLink', '==', null),
        orderBy('created_timestamp', 'desc'),
        limit(limitCount),
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const rawCount = snapshot.docs.length;
      const items: JsonObject[] = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(item => item.id !== excludeMediaId);

      return {
        items,
        hasMore: rawCount === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      // Deliberately differs from getUserMedias (which swallows + returns empty):
      // the link-content sheet surfaces the error via errorOnFirstFetchComponent
      // while preserving hasMore so the user can scroll to retry.
      appLogger.error('Error fetching medias to link', 'FirestoreService.getMediasToLink', { error: error as Error });
      throw error;
    }
  }

  /**
   * Check if username exists
   * Used for username generation/validation
   */
  async usernameExists(username: string, currentUserId?: string): Promise<boolean> {
    try {
      const usersRef = collection(db, 'users');
      const constraints = currentUserId
        ? [where('username', '==', username),
        where('firebase_id', '!=', currentUserId)]
        : [where('username', '==', username)];
      const q = query(usersRef, ...constraints);
      const querySnapshot = await getDocs(q);

      return !querySnapshot.empty;
    } catch (error) {
      appLogger.error('Error checking username', 'FirestoreService.usernameExists', { error: error as Error });
      throw error;
    }
  }

  /**
   * Get user by username
   */
  async getUserByUsername(username: string): Promise<JsonObject | null> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', username));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        return {
          id: userDoc.id,
          ...userDoc.data()
        };
      }

      return null;
    } catch (error) {
      appLogger.error('Error getting user by username', 'FirestoreService.getUserByUsername', { error: error as Error });
      throw error;
    }
  }

  /**
   * Get multiple documents from a collection
   * Generic helper method
   */
  async getCollection(collectionPath: string): Promise<JsonObject[]> {
    try {
      const collectionRef = collection(db, collectionPath);
      const snapshot = await getDocs(collectionRef);

      const documents: JsonObject[] = [];
      snapshot.forEach((doc) => {
        documents.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return documents;
    } catch (error) {
      appLogger.error(`Error fetching collection ${collectionPath}`, 'FirestoreService.getCollection', { error: error as Error });
      throw error;
    }
  }

  /**
   * Get a single document
   * Generic helper method
   */
  async getDocument(collectionPath: string, documentId: string): Promise<JsonObject | null> {
    try {
      const docRef = doc(db, collectionPath, documentId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      }

      return null;
    } catch (error) {
      appLogger.error(`Error fetching document ${collectionPath}/${documentId}`, 'FirestoreService.getDocument', { error: error as Error });
      throw error;
    }
  }

  /**
   * Import content report items to Firestore
   * Matches Flutter's MigrateDataSource.importContentReportItems
   * Path: /content_report_items/{item.id}
   */
  async importContentReportItems(items: Array<{ id: string;[key: string]: unknown }>): Promise<boolean> {
    try {
      const batch = writeBatch(db);

      for (const item of items) {
        const docRef = doc(db, 'content_report_items', item.id);
        batch.set(docRef, item, { merge: true });
      }

      await batch.commit();
      return true;
    } catch (error) {
      appLogger.error('Error importing content report items', 'FirestoreService.importContentReportItems', { error: error as Error });
      return false;
    }
  }

  /**
   * Import user report items to Firestore
   * Matches Flutter's MigrateDataSource.importUserReportItems
   * Path: /user_report_items/{item.id}
   */
  async importUserReportItems(items: Array<{ id: string;[key: string]: unknown }>): Promise<boolean> {
    try {
      const batch = writeBatch(db);

      for (const item of items) {
        const docRef = doc(db, 'user_report_items', item.id);
        batch.set(docRef, item, { merge: true });
      }

      await batch.commit();
      return true;
    } catch (error) {
      appLogger.error('Error importing user report items', 'FirestoreService.importUserReportItems', { error: error as Error });
      return false;
    }
  }

  /**
   * Batch write documents to Firestore
   * Used for operations like analytics logging that need to minimize write costs
   *
   * @param collectionPath - Path to the collection
   * @param documents - Array of documents to write (will auto-generate IDs)
   */
  async batchWrite(collectionPath: string, documents: JsonObject[]): Promise<void> {
    const batch = writeBatch(db);
    const collectionRef = collection(db, collectionPath);

    documents.forEach((docData) => {
      // Remove undefined fields (Firestore doesn't accept undefined values)
      const cleanedData = this.removeUndefinedFields(docData);
      const docRef = doc(collectionRef);
      batch.set(docRef, cleanedData);
    });

    // No try/catch + appLogger here. The only caller is analyticsService.flush,
    // and routing the error through appLogger would re-enter the analytics
    // queue from inside the flush — infinite loop on permission failures.
    // Callers handle/log failures themselves.
    await batch.commit();
  }

  /**
   * Remove undefined fields from an object (Firestore doesn't accept undefined).
   * Recursively processes nested objects AND arrays — array elements that are
   * undefined are dropped (preferred over sending null per the project rule).
   */
  private removeUndefinedFields(obj: JsonObject): JsonObject {
    const cleaned: JsonObject = {};
    for (const key in obj) {
      const sanitized = this.cleanFirestoreValue(obj[key]);
      if (sanitized !== undefined) {
        cleaned[key] = sanitized;
      }
    }
    return cleaned;
  }

  private cleanFirestoreValue(value: unknown): unknown {
    if (value === undefined) return undefined;
    if (value === null) return null;
    if (value instanceof Date) return value;
    if (Array.isArray(value)) {
      return value
        .map(v => this.cleanFirestoreValue(v))
        .filter(v => v !== undefined);
    }
    if (typeof value === 'object') {
      return this.removeUndefinedFields(value as JsonObject);
    }
    return value;
  }

  // ============================================
  // SYNC METHODS
  // Methods for syncing user data to local storage
  // ============================================

  /**
   * Get user's content likes (paginated)
   * Path: /users/{userId}/likes
   */
  async getSyncContentLikes(
    userId: string,
    cursor?: PageCursor,
    limitCount: number = 100
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const likesRef = collection(db, 'users', userId, 'likes');

      let q = query(
        likesRef,
        where('liked', '==', true),
        orderBy('createdDate', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching sync content likes', 'FirestoreService.getSyncContentLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get user's comment likes (paginated)
   * Path: /users/{userId}/comment_likes
   */
  async getSyncCommentLikes(
    userId: string,
    cursor?: PageCursor,
    limitCount: number = 100
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const likesRef = collection(db, 'users', userId, 'comment_likes');

      let q = query(
        likesRef,
        where('liked', '==', true),
        orderBy('createdDate', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching sync comment likes', 'FirestoreService.getSyncCommentLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get user's community message likes (paginated)
   * Path: /users/{userId}/community_message_likes
   */
  async getSyncCommunityMessageLikes(
    userId: string,
    cursor?: PageCursor,
    limitCount: number = 100
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const likesRef = collection(db, 'users', userId, 'community_message_likes');

      let q = query(
        likesRef,
        where('liked', '==', true),
        orderBy('createdDate', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching sync community message likes', 'FirestoreService.getSyncCommunityMessageLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get user's saved content (paginated)
   * Path: /users/{userId}/savedContent
   */
  async getSyncSavedContent(
    userId: string,
    cursor?: PageCursor,
    limitCount: number = 100
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const savedRef = collection(db, 'users', userId, 'savedContent');

      let q = query(
        savedRef,
        orderBy('createdDate', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching sync saved content', 'FirestoreService.getSyncSavedContent', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get user's saved users (paginated)
   * Path: /users/{userId}/savedUsers
   */
  async getSyncSavedUsers(
    userId: string,
    cursor?: PageCursor,
    limitCount: number = 100
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const savedRef = collection(db, 'users', userId, 'savedUsers');

      let q = query(
        savedRef,
        orderBy('createdDate', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching sync saved users', 'FirestoreService.getSyncSavedUsers', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get user's unlocked content (paginated)
   * Path: /users/{userId}/unlocked_content
   */
  async getSyncUnlockedContent(
    userId: string,
    cursor?: PageCursor,
    limitCount: number = 100
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const unlockedRef = collection(db, 'users', userId, 'unlocked_content');

      let q = query(
        unlockedRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching sync unlocked content', 'FirestoreService.getSyncUnlockedContent', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get user's exclusive content subscriptions
   * Path: /users/{userId}/subscriptions
   * Note: Flutter uses 'subscriptions' collection, not 'exclusiveSubscriptions'
   */
  async getSyncExclusiveContentSubscriptions(userId: string, limitCount: number = 100): Promise<JsonObject[]> {
    try {
      const subsRef = collection(db, 'users', userId, 'subscriptions');
      // Flutter filters by meSubscriber: true and status: active(1) or cancelled(3)
      // For sync, we get subscriptions where the current user is the subscriber
      const q = query(
        subsRef,
        where('meSubscriber', '==', true),
        where('status', 'in',
          [ExclusiveContentSubscriptionStatus.active,
          ExclusiveContentSubscriptionStatus.cancelled]),
        orderBy('updatedDate', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      appLogger.error('Error fetching sync exclusive subscriptions', 'FirestoreService.getSyncExclusiveContentSubscriptions', { error: error as Error });
      return [];
    }
  }

  /**
   * Get user's premium packages
   * Path: /users/{userId}/premiumPackages
   */
  async getSyncUserPremiumPackages(userId: string): Promise<JsonObject[]> {
    try {
      const packagesRef = collection(db, 'users', userId, 'premium_packages');
      const q = query(packagesRef, orderBy('createdDate', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      appLogger.error('Error fetching sync user premium packages', 'FirestoreService.getSyncUserPremiumPackages', { error: error as Error });
      return [];
    }
  }

  /**
   * Get available premium packages (app-level)
   * Path: /premiumPackages
   */
  async getSyncPremiumPackages(): Promise<JsonObject[]> {
    try {
      const packagesRef = collection(db, 'premium_packages');
      const q = query(packagesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      appLogger.error('Error fetching sync premium packages', 'FirestoreService.getSyncPremiumPackages', { error: error as Error });
      return [];
    }
  }

  /**
   * Get available gifts (app-level)
   * Path: /gifts
   */
  async getSyncGifts(): Promise<JsonObject[]> {
    try {
      const giftsRef = collection(db, 'gifts');
      const q = query(giftsRef,
        where('active', '==', true),
        orderBy('coins', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      appLogger.error('Error fetching sync gifts', 'FirestoreService.getSyncGifts', { error: error as Error });
      return [];
    }
  }

  /**
   * Get recharge wallet products (app-level)
   * Path: /recharge_wallet_products
   */
  async getSyncRechargeWalletProducts(): Promise<JsonObject[]> {
    try {
      const productsRef = collection(db, 'recharge_wallet_products');
      const q = query(productsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      appLogger.error('Error fetching sync recharge products', 'FirestoreService.getSyncRechargeWalletProducts', { error: error as Error });
      return [];
    }
  }

  /**
   * Get donation products (app-level)
   * Path: /donation_products
   */
  async getSyncDonationProducts(): Promise<JsonObject[]> {
    try {
      const productsRef = collection(db, 'donation_products');
      const q = query(productsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      appLogger.error('Error fetching sync donation products', 'FirestoreService.getSyncDonationProducts', { error: error as Error });
      return [];
    }
  }

  /**
   * Get user's wallet data
   * Path: /wallet/{userId}
   */
  async getSyncWallet(userId: string): Promise<JsonObject | null> {
    try {
      const walletRef = doc(db, 'wallet', userId);
      const walletDoc = await getDoc(walletRef);

      if (!walletDoc.exists()) {
        return null;
      }

      return { id: walletDoc.id, ...walletDoc.data() };
    } catch (error) {
      appLogger.error('Error fetching sync wallet', 'FirestoreService.getSyncWallet', { error: error as Error });
      return null;
    }
  }

  /**
   * Get user's roadmap likes (paginated)
   * Path: /users/{userId}/roadmap_likes
   */
  async getSyncRoadmapLikes(
    userId: string,
    cursor?: PageCursor,
    limitCount: number = 100
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const likesRef = collection(db, 'users', userId, 'roadmap_likes');

      let q = query(
        likesRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching sync roadmap likes', 'FirestoreService.getSyncRoadmapLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get user's roadmap comment likes (paginated)
   * Path: /users/{userId}/roadmap_comment_likes
   */
  async getSyncRoadmapCommentLikes(
    userId: string,
    cursor?: PageCursor,
    limitCount: number = 100
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const likesRef = collection(db, 'users', userId, 'roadmap_comment_likes');

      let q = query(
        likesRef,
        orderBy('createdDate', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching sync roadmap comment likes', 'FirestoreService.getSyncRoadmapCommentLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get roadmap items (paginated).
   * Flutter: RoadmapDataSource.getRoadmap (lib/data/data_sources/roadmap_data_source.dart:40-75).
   * Collection-group query over `roadmap`, ordered by `order` asc then `createdTimestamp` desc.
   */
  async getRoadmap(
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const roadmapRef = collectionGroup(db, 'roadmap');

      let q = query(
        roadmapRef,
        orderBy('order'),
        orderBy('createdTimestamp', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching roadmap', 'FirestoreService.getRoadmap', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Paginated fetch of users who liked (or disliked) a roadmap item via collectionGroup('roadmap_likes').
   * Filters by `roadmapItemId` and, when `liked`, `liked == true`, otherwise `disliked == true`,
   * ordered by `timestamp` desc.
   * Mirrors Flutter `RoadmapDataSource.getRoadmapItemLikes`
   * (lib/data/data_sources/roadmap_data_source.dart:534-578).
   */
  async getRoadmapItemLikes(
    roadmapItemId: string,
    liked: boolean,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const likesRef = collectionGroup(db, 'roadmap_likes');
      let q = query(
        likesRef,
        where('roadmapItemId', '==', roadmapItemId),
        where(liked ? 'liked' : 'disliked', '==', true),
        orderBy('timestamp', 'desc'),
      );
      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }
      q = query(q, limit(limitCount));

      const snapshot = await getDocs(q);
      const items: JsonObject[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching roadmap item likes', 'FirestoreService.getRoadmapItemLikes', {
        context: { roadmapItemId, liked },
        error: error as Error,
      });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get a single roadmap item by id (opened from a notification).
   * Flutter: RoadmapDataSource.getNotificationRoadmapItem (lib/data/data_sources/roadmap_data_source.dart:103-130).
   * Reads the top-level `roadmap` collection by document id; returns a single-item response (or empty).
   */
  async getNotificationRoadmapItem(
    roadmapItemId: string
  ): Promise<{ items: JsonObject[]; hasMore: boolean }> {
    try {
      const snapshot = await getDoc(doc(db, 'roadmap', roadmapItemId));

      if (!snapshot.exists()) {
        return { items: [], hasMore: false };
      }

      return {
        items: [{ id: snapshot.id, ...snapshot.data() }],
        hasMore: false,
      };
    } catch (error) {
      appLogger.error('Error fetching notification roadmap item', 'FirestoreService.getNotificationRoadmapItem', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get the most recently created roadmap item for a user.
   * Flutter: RoadmapDataSource.getLatestUserRoadmapItem (lib/data/data_sources/roadmap_data_source.dart:132-161).
   * Queries the top-level `roadmap` collection where `userId == userId`, ordered by
   * `createdTimestamp` desc, and returns the first document (or null when none).
   */
  async getLatestUserRoadmapItem(userId: string): Promise<JsonObject | null> {
    try {
      const q = query(
        collection(db, 'roadmap'),
        where('userId', '==', userId),
        orderBy('createdTimestamp', 'desc')
      );

      const snapshot = await getDocs(q);
      const firstDoc = snapshot.docs[0];

      if (!firstDoc) {
        return null;
      }

      return { id: firstDoc.id, ...firstDoc.data() };
    } catch (error) {
      appLogger.error('Error fetching latest user roadmap item', 'FirestoreService.getLatestUserRoadmapItem', { error: error as Error });
      return null;
    }
  }

  // ============================================
  // SUBSCRIPTION METHODS
  // ============================================

  /**
   * Fetch exclusive content subscriptions with pagination
   * Path: /users/{currentUserId}/subscriptions
   * Filters by status (active=1, cancelled=3) and optional meSubscriber
   *
   * @returns JsonObject[], hasMore flag, and pagination cursor
   */
  async getExclusiveContentSubscriptions(
    currentUserId: string,
    cursor?: PageCursor,
    limitCount: number = 20,
    meSubscriber?: boolean | null
  ): Promise<{
    items: JsonObject[];
    hasMore: boolean;
    cursor?: PageCursor;
  }> {
    try {
      const subscriptionsRef = collection(db, 'users', currentUserId, 'subscriptions');

      // Start with status filter (active=1, cancelled=3)
      let q = query(
        subscriptionsRef,
        where('status', 'in', [
          ExclusiveContentSubscriptionStatus.active,
          ExclusiveContentSubscriptionStatus.cancelled,
        ])
      );

      // Add optional meSubscriber filter
      if (meSubscriber !== null && meSubscriber !== undefined) {
        q = query(q, where('meSubscriber', '==', meSubscriber));
      }

      // Order by updatedDate (matches Flutter)
      q = query(q, orderBy('updatedDate', 'desc'));

      // Apply limit
      q = query(q, limit(limitCount));

      // Apply pagination cursor
      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((d) => d.data());

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined,
      };
    } catch (error) {
      appLogger.error('Error fetching subscriptions', 'FirestoreService.getExclusiveContentSubscriptions', { error: error as Error });
      throw error;
    }
  }

  /**
   * Search subscriptions by keyword
   * Uses range queries on firstLastNameLower or lastFirstNameLower
   * Also filters by status (active=1, cancelled=3)
   *
   * @returns JsonObject[], hasMore flag, and pagination cursor
   */
  async searchSubscriptions(
    currentUserId: string,
    keyword: string,
    cursor?: PageCursor,
    limitCount: number = 20,
    searchLastName: boolean = false
  ): Promise<{
    items: JsonObject[];
    hasMore: boolean;
    cursor?: PageCursor;
  }> {
    try {
      // Use camelCase field names (matches Flutter)
      const field = searchLastName ? 'lastFirstNameLower' : 'firstLastNameLower';
      const fieldValue = keyword.toLowerCase().replace(/\s/g, '');

      // Calculate search limit (matches Flutter logic)
      const strFrontCode = fieldValue.substring(0, fieldValue.length - 1);
      const strEndCode = fieldValue[fieldValue.length - 1];
      const searchLimit = strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

      const subscriptionsRef = collection(db, 'users', currentUserId, 'subscriptions');

      // Build query with status filter and range query
      let q = query(
        subscriptionsRef,
        where('status', 'in', [
          ExclusiveContentSubscriptionStatus.active,
          ExclusiveContentSubscriptionStatus.cancelled,
        ]),
        where(field, '>=', fieldValue),
        where(field, '<', searchLimit),
        orderBy(field),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((d) => d.data());

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined,
      };
    } catch (error) {
      appLogger.error('Error searching subscriptions', 'FirestoreService.searchSubscriptions', { error: error as Error });
      throw error;
    }
  }

  /**
   * Check whether the peer (`peerId`) is subscribed to the current user's exclusive content.
   *
   * Reads `users/{peerId}/subscriptions` for any doc where:
   *   - `userId == currentFirebaseId` (the subscriber is the current user's id stored under the peer's subscriptions)
   *   - `status in [active, cancelled]`
   *
   * Status is the INTEGER ORDINAL of the Dart enum
   * (`requested=0, active=1, inactive=2, cancelled=3`) — NOT a string. Stored that
   * way by Flutter via `ExclusiveContentSubscriptionStatus.index` (see
   * `lib/data/data_sources/content_subscriptions_data_source.dart:51-78` and
   * `lib/domain/enums/exclusive_content_subscription_status.dart`). Reuses the
   * existing `ExclusiveContentSubscriptionStatus` numeric enum already used by
   * `getSyncExclusiveContentSubscriptions`, `getExclusiveContentSubscriptions`,
   * and `searchSubscriptions`.
   *
   * `firestoreService` has no existing convention for resolving the current
   * Firebase user id (no `auth.currentUser?.uid` callers exist in this file),
   * so `currentFirebaseId` is accepted as a parameter and resolved by the
   * domain service that calls this.
   *
   * @param peerId The peer whose subscriptions collection to inspect.
   * @param currentFirebaseId The current user's Firebase uid.
   * @returns `true` if at least one matching subscription document exists.
   */
  async peerIsSubscribedToCurrentUser(
    peerId: string,
    currentFirebaseId: string,
  ): Promise<boolean> {
    try {
      const subscriptionsRef = collection(db, 'users', peerId, 'subscriptions');
      const q = query(
        subscriptionsRef,
        where('userId', '==', currentFirebaseId),
        where('status', 'in', [
          ExclusiveContentSubscriptionStatus.active,
          ExclusiveContentSubscriptionStatus.cancelled,
        ]),
      );
      const snap = await getDocs(q);
      return !snap.empty;
    } catch (error) {
      appLogger.error(
        'Error checking peer subscription to current user',
        'FirestoreService.peerIsSubscribedToCurrentUser',
        { error: error as Error },
      );
      return false;
    }
  }

  // ============================================
  // CHAT METHODS
  // ============================================

  /**
   * Save batch chat messages to Firestore
   * Writes to:
   * - /message_nodes/{groupChatId}/messages/{messageId} - Individual messages
   * - /message_nodes/{groupChatId} - Chat metadata (merged)
   *
   * @param messages - Array of { groupChatId, messageId, messageData, metadataData }
   * @returns Promise<boolean> - true if successful, false otherwise
   */
  async saveBatchChatMessages(
    messages: Array<{
      groupChatId: string;
      messageId: string;
      messageData: JsonObject;
      metadataData: JsonObject;
    }>
  ): Promise<boolean> {
    try {
      const batch = writeBatch(db);

      for (const msg of messages) {
        // Write message document
        const messageRef = doc(
          db,
          'message_nodes',
          msg.groupChatId,
          'messages',
          msg.messageId
        );
        batch.set(messageRef, msg.messageData);

        // Write/merge chat metadata
        const chatNodeRef = doc(db, 'message_nodes', msg.groupChatId);
        batch.set(chatNodeRef, msg.metadataData, { merge: true });
      }

      await batch.commit();
      return true;
    } catch (error) {
      appLogger.error('Error saving batch chat messages', 'FirestoreService.saveBatchChatMessages', { error: error as Error });
      return false;
    }
  }

  /**
   * Fetch one page of chat messages, newest-first.
   *
   * Path: /message_nodes/{groupChatId}/messages
   * Order: timestamp desc
   * Pagination: value-based cursor on the ordered field (the `timestamp` of the
   * last document in the previous page). This is equivalent to Flutter's
   * `startAfterDocument(_lastFetchedDocument)` for a single-field desc-ordered
   * query, but keeps the data-layer surface JSON-only — the caller never holds
   * a Firestore snapshot reference (data-layer surface rule, data-layer.md).
   *
   * Matches Flutter: lib/presentation/screens/chat/chat_screen.dart:407-421
   * (`FirebaseFirestore.instance.collection('message_nodes').doc(groupChatId)
   *   .collection('messages').orderBy('timestamp', descending: true)
   *   .startAfterDocument(_lastFetchedDocument).limit(_documentLimit).get()`).
   *
   * @param groupChatId - The chat's group id (sorted-hashCode join of the two user ids).
   * @param lastTimestamp - The timestamp of the last document from the previous
   *   page, or `null` for the first page.
   * @param limit - Page size (typically `SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION = 20`).
   * @returns `messages` (raw Firestore JSON, newest-first) and `lastTimestamp`
   *   (the timestamp of the last doc in this page, or `null` if the page is empty).
   *   `hasMore` derivation is the caller's responsibility (compare returned length
   *   to `limit`) — kept out of this layer to match the rest of the service.
   */
  async fetchChatMessagesPage(
    groupChatId: string,
    lastTimestamp: number | null,
    limitCount: number,
  ): Promise<{ messages: JsonObject[]; lastTimestamp: number | null }> {
    try {
      const messagesRef = collection(db, 'message_nodes', groupChatId, 'messages');

      let q = query(
        messagesRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount),
      );

      if (lastTimestamp !== null) {
        q = query(q, startAfter(lastTimestamp));
      }

      const snapshot = await getDocs(q);
      const messages: JsonObject[] = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      const tail = snapshot.docs[snapshot.docs.length - 1];
      const nextLastTimestamp = tail
        ? ((tail.data() as { timestamp?: number }).timestamp ?? null)
        : null;

      return { messages, lastTimestamp: nextLastTimestamp };
    } catch (error) {
      appLogger.error(
        'Error fetching chat messages page',
        'FirestoreService.fetchChatMessagesPage',
        { error: error as Error },
      );
      // Surface to caller — the chat hook needs to distinguish first-load
      // failure (show error state) from an empty page (show empty state).
      throw error;
    }
  }

  /**
   * Subscribe to live chat-message changes arriving after `sinceTimestamp`.
   *
   * Path: /message_nodes/{groupChatId}/messages
   * Filter: where('timestamp', '>', sinceTimestamp)
   *
   * Mirrors Flutter's `listenToChatUpdates`
   * (lib/presentation/screens/chat/chat_screen.dart:489-495):
   *   FirebaseFirestore.instance
   *     .collection('message_nodes')
   *     .doc(groupChatId)
   *     .collection('messages')
   *     .where('timestamp', isGreaterThan: timestampChatObservation)
   *     .snapshots();
   *
   * Emits the raw doc data (with the document id spread in as `id`) so the
   * domain layer's `ChatMessageRemapper.fromJson` can build the entity.
   *
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToChatMessages(
    groupChatId: string,
    sinceTimestamp: number,
    onChange: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collection(db, 'message_nodes', groupChatId, 'messages'),
      where('timestamp', '>', sinceTimestamp),
    );
    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: { id: c.doc.id, ...c.doc.data() },
        }));
        if (changes.length > 0) onChange(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Get a page of community-wall messages for the creator `owner`,
   * newest-first, using a value cursor (the previous page's tail
   * `createdTimestamp`) so the data-layer surface stays JSON-only rather than
   * leaking a Firestore snapshot reference (data-layer surface rule,
   * data-layer.md).
   *
   * Path:     /users/{owner}/community_messages
   * Ordering: orderBy('createdTimestamp', 'desc')
   * Cursor:   startAfter(lastCreatedTimestamp) when non-null
   *
   * Matches Flutter: lib/data/data_sources/community_data_source.dart:46-83
   * (`CommunityDataSource.getCommunityMessages`):
   *   FirebaseFirestore.instance.collection('users').doc(owner)
   *     .collection('community_messages')
   *     .orderBy('createdTimestamp', descending: true)
   *     .startAfterDocument(lastFetchedMedia).limit(limit).get()
   *
   * @param owner - The creator's firebaseId whose wall is being read.
   * @param lastCreatedTimestamp - The `createdTimestamp` of the last document
   *   from the previous page, or `null` for the first page.
   * @param limitCount - Page size.
   * @returns `messages` (raw Firestore JSON, newest-first) and `lastTimestamp`
   *   (the `createdTimestamp` of the last doc in this page, or `null` if empty).
   *   `hasMore` derivation is the caller's responsibility (compare returned
   *   length to `limitCount`) — kept out of this layer to match the rest of the
   *   service.
   */
  async fetchCommunityMessagesPage(
    owner: string,
    lastCreatedTimestamp: number | null,
    limitCount: number,
  ): Promise<{ messages: JsonObject[]; lastTimestamp: number | null }> {
    try {
      const messagesRef = collection(db, 'users', owner, 'community_messages');

      let q = query(
        messagesRef,
        orderBy('createdTimestamp', 'desc'),
        limit(limitCount),
      );

      if (lastCreatedTimestamp !== null) {
        q = query(q, startAfter(lastCreatedTimestamp));
      }

      const snapshot = await getDocs(q);
      // Flutter reads `json.data()`, which already contains the stored `id`
      // field — spread the doc data and rely on that stored `id`.
      const messages: JsonObject[] = snapshot.docs.map((d) => ({ ...d.data() }));

      const tail = snapshot.docs[snapshot.docs.length - 1];
      const nextLastTimestamp = tail
        ? ((tail.data() as { createdTimestamp?: number }).createdTimestamp ?? null)
        : null;

      return { messages, lastTimestamp: nextLastTimestamp };
    } catch (error) {
      appLogger.error(
        'Error fetching community messages page',
        'FirestoreService.fetchCommunityMessagesPage',
        { error: error as Error },
      );
      // Surface to caller — the community hook needs to distinguish first-load
      // failure (show error state) from an empty page (show empty state).
      throw error;
    }
  }

  /**
   * Subscribe to live community-wall message changes for the creator `owner`
   * arriving after `sinceCreatedTimestamp`.
   *
   * CollectionGroup: community_messages
   * Filter: where('messageUsersInfo.owner', '==', owner)
   *         AND where('createdTimestamp', '>', sinceCreatedTimestamp)
   *
   * Mirrors Flutter's `subscribeToLiveMessages`
   * (lib/presentation/screens/community/community_screen.dart:286-291):
   *   FirebaseFirestore.instance
   *     .collectionGroup('community_messages')
   *     .where('messageUsersInfo.owner', isEqualTo: owner)
   *     .where('createdTimestamp', isGreaterThan: timestampChatObservation)
   *     .snapshots();
   *
   * Both clauses are required — a single-clause port is a parity bug
   * (community_screen.dart:288-290).
   *
   * The nested-field equality combined with the `createdTimestamp` range filter
   * relies on a Firestore composite index; the Flutter app already issues this
   * exact query so the index exists in the shared project — no web-side index
   * change is authored here.
   *
   * Emits the raw doc data (spread) so the domain layer's
   * `CommunityChatMessageRemapper.fromJson` can build the entity.
   *
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToCommunityMessages(
    owner: string,
    sinceCreatedTimestamp: number,
    onChange: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collectionGroup(db, 'community_messages'),
      where('messageUsersInfo.owner', '==', owner),
      where('createdTimestamp', '>', sinceCreatedTimestamp),
    );
    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: { ...c.doc.data() },
        }));
        if (changes.length > 0) onChange(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Subscribe to the single `videoCall` chat message identified by its
   * `videoCallId`, surfacing every doc change so the consumer can react to the
   * call lifecycle (audience joined / call ended).
   *
   * Path:   /message_nodes/{groupChatId}/messages
   * Filter: where('videoCallId', '==', callMessageId)
   * Limit:  1
   *
   * Mirrors Flutter's `_subscribeToVideoCallMessage`
   * (lib/presentation/screens/chat/video_call_screen.dart:347-355):
   *   FirebaseFirestore.instance
   *     .collection('message_nodes')
   *     .doc(widget.groupChatId)
   *     .collection('messages')
   *     .where('videoCallId', isEqualTo: widget.callMessageId)
   *     .limit(1)
   *     .snapshots();
   *
   * Flutter gates on `change.type == DocumentChangeType.modified`
   * (video_call_screen.dart:361). That `modified`-only gate is the consumer's
   * concern and lands in the Task-6 use case — this method stays a thin data
   * proxy and surfaces ALL change types as raw `JsonObject` (doc id spread in
   * as `id`) so the domain `ChatMessageRemapper.fromJson` can build the entity.
   *
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToVideoCallMessage(
    groupChatId: string,
    callMessageId: string,
    onChange: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collection(db, 'message_nodes', groupChatId, 'messages'),
      where('videoCallId', '==', callMessageId),
      limit(1),
    );
    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: { id: c.doc.id, ...c.doc.data() },
        }));
        if (changes.length > 0) onChange(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Add a single community-wall message for the creator `owner`.
   *
   * Path: /users/{owner}/community_messages/{messageId}
   * Write: set(messageData, { merge: true })
   *
   * Matches Flutter: lib/data/data_sources/community_data_source.dart:126-165
   * (`CommunityDataSource.addCommunityMessage`):
   *   transaction.set(communityMessageReference, message.toJson(),
   *     SetOptions(merge: true))
   *
   * Flutter wraps the write in a `runTransaction` because it conditionally
   * writes a second `notifications` doc for a `go-live` message. That
   * notification branch is LIVE-only and out of scope (task-prompt line 2), so
   * only the message doc is written here; a single `set(merge)` is
   * parity-equivalent to Flutter's one-doc transaction when there is no second
   * doc.
   *
   * The payload is built by the domain service (output of
   * `CommunityChatMessageRemapper.toJson`) — this data-layer method takes an
   * opaque `JsonObject` to keep the data-layer surface JSON-only (data-layer.md
   * surface rule).
   *
   * @param owner - The creator's firebaseId (`message.messageUsersInfo.owner`).
   * @param messageId - The message document id (`message.id`).
   * @param messageData - The serialized message payload.
   * @returns Promise<boolean> - true if successful, false otherwise (parity with
   *   Flutter's `bool success` pattern at `community_data_source.dart:133, 158, 161`).
   */
  async addCommunityMessage(
    owner: string,
    messageId: string,
    messageData: JsonObject,
  ): Promise<boolean> {
    try {
      const messageRef = doc(db, 'users', owner, 'community_messages', messageId);
      await setDoc(messageRef, messageData, { merge: true });
      return true;
    } catch (error) {
      appLogger.error(
        'Error adding community message',
        'FirestoreService.addCommunityMessage',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Get a page of a live session's chat messages, newest-first, from the
   * `live_messages` collection-group filtered by `parentId` (the live
   * streaming message id). Uses a value cursor (the previous page's tail
   * `createdTimestamp`) so the data-layer surface stays JSON-only rather than
   * leaking a Firestore snapshot reference (data-layer surface rule,
   * data-layer.md) — a documented divergence from Flutter's
   * `startAfterDocument` snapshot cursor, mirroring the wall fetch
   * (`fetchCommunityMessagesPage` above).
   *
   * CollectionGroup: live_messages
   * Filter:          where('parentId', '==', liveMessageId)
   * Ordering:        orderBy('createdTimestamp', 'desc')
   * Cursor:          startAfter(lastCreatedTimestamp) when non-null
   *
   * Matches Flutter: lib/data/data_sources/community_data_source.dart:85-124
   * (`CommunityDataSource.getCommunityLiveMessages`):
   *   FirebaseFirestore.instance
   *     .collectionGroup('live_messages')
   *     .where("parentId", isEqualTo: liveMessageId)
   *     .orderBy("createdTimestamp", descending: true)
   *     .startAfterDocument(lastFetchedMedia).limit(limit).get()
   *
   * The collection-group equality + `createdTimestamp` ordering relies on a
   * Firestore composite index; the LIVE Flutter app already issues this exact
   * query so the index exists in the shared project — no web-side index change
   * is authored here (same note the wall subscribe / like proxies carry,
   * firestoreService.ts subscribeToCommunityMessages).
   *
   * @param liveMessageId - The live streaming message id (`parentId` of every
   *   live message in the session).
   * @param lastCreatedTimestamp - The `createdTimestamp` of the last document
   *   from the previous page, or `null` for the first page.
   * @param limitCount - Page size.
   * @returns `messages` (raw Firestore JSON, newest-first) and `lastTimestamp`
   *   (the `createdTimestamp` of the last doc in this page, or `null` if empty).
   *   `hasMore` derivation is the caller's responsibility (compare returned
   *   length to `limitCount`, Flutter's strict `== limit` at :115) — kept out of
   *   this layer to match the wall fetch.
   */
  async fetchCommunityLiveMessagesPage(
    liveMessageId: string,
    lastCreatedTimestamp: number | null,
    limitCount: number,
  ): Promise<{ messages: JsonObject[]; lastTimestamp: number | null }> {
    try {
      let q = query(
        collectionGroup(db, 'live_messages'),
        where('parentId', '==', liveMessageId),
        orderBy('createdTimestamp', 'desc'),
        limit(limitCount),
      );

      if (lastCreatedTimestamp !== null) {
        q = query(q, startAfter(lastCreatedTimestamp));
      }

      const snapshot = await getDocs(q);
      // Flutter reads `json.data()`, which already contains the stored `id`
      // field — spread the doc data and rely on that stored `id`.
      const messages: JsonObject[] = snapshot.docs.map((d) => ({ ...d.data() }));

      const tail = snapshot.docs[snapshot.docs.length - 1];
      const nextLastTimestamp = tail
        ? ((tail.data() as { createdTimestamp?: number }).createdTimestamp ?? null)
        : null;

      return { messages, lastTimestamp: nextLastTimestamp };
    } catch (error) {
      appLogger.error(
        'Error fetching community live messages page',
        'FirestoreService.fetchCommunityLiveMessagesPage',
        { error: error as Error },
      );
      // Surface to caller — the live chat hook needs to distinguish first-load
      // failure (show error state) from an empty page (show empty state), the
      // same distinction the wall fetch preserves.
      throw error;
    }
  }

  /**
   * Subscribe to a live session's chat message changes arriving after
   * `sinceCreatedTimestamp`.
   *
   * CollectionGroup: live_messages
   * Filter:          where('parentId', '==', liveMessageId)
   *                  AND where('createdTimestamp', '>', sinceCreatedTimestamp)
   *
   * Mirrors Flutter's `subscribeToLiveMessages`
   * (lib/presentation/screens/community/live_streaming_screen.dart:622-633):
   *   FirebaseFirestore.instance
   *     .collectionGroup('live_messages')
   *     .where("parentId", isEqualTo: widget.liveStreamingMessageId)
   *     .where("createdTimestamp", isGreaterThan: timestampChatObservation)
   *     .snapshots();
   *
   * Two where clauses and NO `orderBy` (contrast the paginated fetch, which
   * orders) — a single-clause or extra-`orderBy` port is a parity bug
   * (live_streaming_screen.dart:628-633). The `added`/`modified`/`removed`
   * routing of the emitted changes is the consumer's concern (the live chat
   * hook, Task 7), not this proxy.
   *
   * The collection-group equality combined with the `createdTimestamp` range
   * filter relies on a Firestore composite index; the LIVE Flutter app already
   * issues this exact query so the index exists in the shared project — no
   * web-side index change is authored here.
   *
   * Emits the raw doc data (spread) so the domain layer's
   * `CommunityChatMessageRemapper.fromJson` can build the entity.
   *
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToCommunityLiveMessages(
    liveMessageId: string,
    sinceCreatedTimestamp: number,
    onChange: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collectionGroup(db, 'live_messages'),
      where('parentId', '==', liveMessageId),
      where('createdTimestamp', '>', sinceCreatedTimestamp),
    );
    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: { ...c.doc.data() },
        }));
        if (changes.length > 0) onChange(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Add a single live chat message for the session owned by `owner`.
   *
   * Path:  /users/{owner}/community_messages/{parentId}/live_messages/{messageId}
   * Write: set(messageData, { merge: true })
   *
   * Matches Flutter: lib/data/data_sources/community_data_source.dart:167-196
   * (`CommunityDataSource.addCommunityLiveMessage`):
   *   FirebaseFirestore.instance
   *     .collection('users').doc(message.messageUsersInfo.owner)
   *     .collection('community_messages').doc(message.parentId)
   *     .collection('live_messages').doc(message.id)
   *     .set(message.toJson(), SetOptions(merge: true))
   *
   * Unlike the wall write there is no second (notification) doc, so this is a
   * plain `setDoc(merge)` — not a `runTransaction` — which is exact parity with
   * Flutter's single `.set(merge)` (community_data_source.dart:187-188).
   *
   * The payload is built by the domain service (output of
   * `CommunityChatMessageRemapper.toJson` via `fromLiveInput`) — this
   * data-layer method takes an opaque `JsonObject` to keep the data-layer
   * surface JSON-only (data-layer.md surface rule).
   *
   * @param owner - The session owner's firebaseId
   *   (`message.messageUsersInfo.owner`, the broadcaster).
   * @param parentId - The live streaming message id (`message.parentId`).
   * @param messageId - The live message document id (`message.id`).
   * @param messageData - The serialized message payload.
   * @returns Promise<boolean> - true if successful, false otherwise (parity with
   *   Flutter's `bool success` pattern at `community_data_source.dart:173, 189, 192`).
   */
  async addCommunityLiveMessage(
    owner: string,
    parentId: string,
    messageId: string,
    messageData: JsonObject,
  ): Promise<boolean> {
    try {
      const messageRef = doc(
        db,
        'users',
        owner,
        'community_messages',
        parentId,
        'live_messages',
        messageId,
      );
      await setDoc(messageRef, messageData, { merge: true });
      return true;
    } catch (error) {
      appLogger.error(
        'Error adding community live message',
        'FirestoreService.addCommunityLiveMessage',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Like or unlike a community-wall message via a Firestore transaction.
   *
   * Writes (same two writes, same order as Flutter):
   * - set (merge) users/{userId}/community_message_likes/{communityMessageId}
   *   with the serialized like payload (the CURRENT user's like-doc tree; the
   *   doc id is the target message id).
   * - update the target message's `likes` counter by increment(+1) when
   *   `liked`, increment(-1) when unliked.
   *
   * The target message reference is resolved BEFORE the transaction via a
   * collection-group lookup (Flutter runs this query before `runTransaction`,
   * and the web SDK cannot run queries inside a transaction anyway):
   *   collectionGroup('community_messages')
   *     .where('id', '==', communityMessageId)
   *     .orderBy('createdTimestamp', 'desc')  ->  docs[0].ref
   *
   * Matches Flutter: lib/data/data_sources/community_data_source.dart:9-44
   * (`CommunityDataSource.likeCommunityMessage`):
   *   - :12    FieldValue.increment(like.liked ? 1 : -1)
   *   - :13-16 users/{like.userId}/community_message_likes/{like.communityMessageId}
   *   - :18-23 collectionGroup('community_messages').where('id', ==).orderBy('createdTimestamp', desc)
   *   - :32-34 transaction.set(likeRef, like.toJson(), merge) then transaction.update(messageRef, {likes})
   *
   * The `orderBy('createdTimestamp', 'desc')` is part of Flutter's exact query
   * (:21) and is preserved. The nested collection-group + orderBy combination
   * relies on a Firestore composite index; the Flutter app already issues this
   * exact query so the index exists in the shared project — no web-side index
   * change is authored here (same reasoning as
   * `subscribeToCommunityMessages`, :1548-1552).
   *
   * Divergence (defensive only): Flutter's collection-group lookup sits OUTSIDE
   * its `try` block (:18-23 vs `try` at :30), so a lookup failure propagates
   * uncaught. The web wraps the lookup + an empty-result check in the same
   * try/catch so any failure surfaces as `false` (letting the caller revert its
   * optimistic update + toast). The success path is byte-identical; only the
   * error path is handled more gracefully.
   *
   * The `likeData` payload is built by the domain remapper
   * (`CommunityMessageLikeRemapper.toJson`) — this data-layer method takes an
   * opaque `JsonObject` to keep the data-layer surface JSON-only (data-layer.md
   * surface rule).
   *
   * @param params.userId - The CURRENT user's firebaseId (owner of the like doc).
   * @param params.communityMessageId - Doc id of both the like doc and the
   *   target message.
   * @param params.liked - true likes (+1), false unlikes (-1).
   * @param params.likeData - The serialized `CommunityMessageLike` payload.
   * @returns Promise<boolean> - true on success, false otherwise (parity with
   *   Flutter's `bool success` pattern at `community_data_source.dart:10, 37, 40, 43`).
   */
  async likeCommunityMessage(params: {
    userId: string;
    communityMessageId: string;
    liked: boolean;
    likeData: JsonObject;
  }): Promise<boolean> {
    try {
      const messageSnapshot = await getDocs(
        query(
          collectionGroup(db, 'community_messages'),
          where('id', '==', params.communityMessageId),
          orderBy('createdTimestamp', 'desc'),
        ),
      );
      if (messageSnapshot.empty) {
        appLogger.error(
          'Community message not found while liking',
          'FirestoreService.likeCommunityMessage',
          { context: { communityMessageId: params.communityMessageId } },
        );
        return false;
      }
      const messageRef = messageSnapshot.docs[0].ref;

      const likeRef = doc(
        db,
        'users',
        params.userId,
        'community_message_likes',
        params.communityMessageId,
      );

      return await runTransaction(db, async (transaction) => {
        transaction.set(likeRef, params.likeData, { merge: true });
        transaction.update(messageRef, {
          likes: increment(params.liked ? 1 : -1),
        });
        return true;
      });
    } catch (error) {
      appLogger.error(
        'Error liking community message',
        'FirestoreService.likeCommunityMessage',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Start a community live-streaming session with a single Firestore transaction
   * that atomically writes BOTH the `live` community message AND its
   * `liveStarted` notification.
   *
   * Writes (same two writes, same order as Flutter):
   * - set (merge) users/{owner}/community_messages/{messageId} with the
   *   serialized message payload (carries the `broadcastingInfo` struct with the
   *   broadcaster's resolved Agora streaming id + `isLive: true`).
   * - set (merge) users/{owner}/notifications/{notificationId} with the
   *   serialized `liveStarted` notification payload.
   *
   * Matches Flutter: lib/data/data_sources/community_data_source.dart:126-165
   * (`CommunityDataSource.addCommunityMessage(message, notification)`):
   *   - :138-139 communityMessageReference =
   *       users/{message.messageUsersInfo.owner}/community_messages/{message.id}
   *   - :142-143 notificationLiveBroadcastReference =
   *       users/{owner}/notifications/{notification.id}
   *   - :148-156 runTransaction -> transaction.set(communityMessageReference,
   *       message.toJson(), merge) then transaction.set(
   *       notificationLiveBroadcastReference, notification.toJson(), merge)
   *
   * The notification is written under the SAME `owner` user doc: on the community
   * screen the broadcaster IS the wall owner, so
   * `notification.info.usersInfo.userId == owner` (Flutter reuses the same `user`
   * DocumentReference for both writes at :142-143).
   *
   * Both payloads are serialized in the domain (`LiveStreamingService`, via the
   * `MessageBroadcastingInfoRemapper` + notification `toJson` chain) — this
   * data-layer method takes opaque `JsonObject` payloads to keep the data-layer
   * surface JSON-only (data-layer.md surface rule).
   *
   * @param owner - The broadcaster/wall-owner firebaseId (`message.messageUsersInfo.owner`).
   * @param messageId - The community message document id (`message.id`).
   * @param messageData - The serialized `live` community message payload.
   * @param notificationId - The notification document id (`notification.id`).
   * @param notificationData - The serialized `liveStarted` notification payload.
   * @returns Promise<boolean> - true on success, false otherwise (parity with
   *   Flutter's `bool success` pattern at `community_data_source.dart:133, 158, 161`).
   */
  async startCommunityLiveMessage(
    owner: string,
    messageId: string,
    messageData: JsonObject,
    notificationId: string,
    notificationData: JsonObject,
  ): Promise<boolean> {
    try {
      // Finding 1 (round 5): you can only start a live on your OWN community
      // wall. On the community screen the wall owner IS the broadcaster, so a
      // legitimate start-live always has `owner == auth.currentUser.uid`.
      // Refuse otherwise so an audience user cannot author a `type = live`
      // message under another creator's wall (by accident or client-side hack).
      const currentFirebaseId = auth.currentUser?.uid;
      if (!currentFirebaseId || currentFirebaseId !== owner) {
        appLogger.error(
          'Refusing to start live: owner is not the current authenticated user',
          'FirestoreService.startCommunityLiveMessage',
          { context: { owner, currentFirebaseId } },
        );
        return false;
      }

      const messageRef = doc(db, 'users', owner, 'community_messages', messageId);
      const notificationRef = doc(db, 'users', owner, 'notifications', notificationId);

      return await runTransaction(db, async (transaction) => {
        transaction.set(messageRef, messageData, { merge: true });
        transaction.set(notificationRef, notificationData, { merge: true });
        return true;
      });
    } catch (error) {
      appLogger.error(
        'Error starting community live message',
        'FirestoreService.startCommunityLiveMessage',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * End a community live-streaming session with a single Firestore transaction
   * that atomically merges the message update (`isLive: false`) AND writes the
   * `liveEnded` notification.
   *
   * The target message reference is resolved BEFORE the transaction via a
   * collection-group lookup (Flutter runs this query before `runTransaction`, and
   * the web SDK cannot run queries inside a transaction anyway — same shape as
   * `likeCommunityMessage`):
   *   collectionGroup('community_messages')
   *     .where('id', '==', messageId)
   *     .orderBy('createdTimestamp', 'desc')  ->  docs[0].ref
   *
   * Writes (same two writes, same order as Flutter):
   * - set (merge) messageRef with the `messageUpdate` payload (Flutter builds
   *   `{ text, updatedTimestamp, broadcastingInfo: { isLive: false } }` at
   *   :220-228 — assembled in the domain here).
   * - set (merge) users/{notificationUserId}/notifications/{notificationId} with
   *   the serialized `liveEnded` notification payload.
   *
   * Matches Flutter: lib/data/data_sources/community_data_source.dart:198-245
   * (`CommunityDataSource.endLiveMessage`):
   *   - :207-212 collectionGroup('community_messages').where('id', ==)
   *       .orderBy('createdTimestamp', desc) -> docs.first.reference
   *   - :213-218 notificationLiveBroadcastReference =
   *       users/{notification.info.usersInfo.userId}/notifications/{notification.id}
   *   - :231-236 runTransaction -> transaction.set(messageReference,
   *       messageUpdateInfo, merge) then transaction.set(
   *       notificationLiveBroadcastReference, notification.toJson(), merge)
   *
   * The nested collection-group + orderBy combination relies on a Firestore
   * composite index; the Flutter app already issues this exact query and web
   * `likeCommunityMessage` (:1740-1745) already relies on it in the shared
   * project, so no web-side index change is authored here.
   *
   * Divergence (defensive only, matching `likeCommunityMessage`): the lookup +
   * empty-result guard sit inside the same try/catch, so any failure surfaces as
   * `false` rather than propagating uncaught (Flutter's lookup at :207-212 is
   * outside its `try` at :230). The success path is byte-identical.
   *
   * Both payloads are serialized in the domain (`LiveStreamingService`) — this
   * data-layer method takes opaque `JsonObject` payloads to keep the data-layer
   * surface JSON-only (data-layer.md surface rule).
   *
   * @param messageId - The `id` field of the live message to end (used to locate
   *   the message doc via the collection-group query).
   * @param messageUpdate - The serialized merge payload for the message doc.
   * @param notificationUserId - The notification owner's firebaseId
   *   (`notification.info.usersInfo.userId`).
   * @param notificationId - The notification document id (`notification.id`).
   * @param notificationData - The serialized `liveEnded` notification payload.
   * @returns Promise<boolean> - true on success, false otherwise (parity with
   *   Flutter's `bool success` pattern at `community_data_source.dart:205, 238, 241`).
   */
  async endCommunityLiveMessage(
    messageId: string,
    messageUpdate: JsonObject,
    notificationUserId: string,
    notificationId: string,
    notificationData: JsonObject,
  ): Promise<boolean> {
    try {
      const messageSnapshot = await getDocs(
        query(
          collectionGroup(db, 'community_messages'),
          where('id', '==', messageId),
          orderBy('createdTimestamp', 'desc'),
        ),
      );
      if (messageSnapshot.empty) {
        appLogger.error(
          'Community live message not found while ending live',
          'FirestoreService.endCommunityLiveMessage',
          { context: { messageId } },
        );
        return false;
      }
      const messageDoc = messageSnapshot.docs[0];
      const messageRef = messageDoc.ref;

      const data = messageDoc.data() as {
        messageUsersInfo?: { owner?: string };
        broadcastingInfo?: { isLive?: boolean };
      };
      const currentFirebaseId = auth.currentUser?.uid;

      // (A) only the broadcaster (== wall owner) may end the session, so an
      // audience user cannot end the live by mistake.
      if (
        !currentFirebaseId ||
        data.messageUsersInfo?.owner !== currentFirebaseId
      ) {
        appLogger.error(
          'Refusing to end live: current user is not the broadcaster',
          'FirestoreService.endCommunityLiveMessage',
          {
            context: {
              messageId,
              currentFirebaseId,
              owner: data.messageUsersInfo?.owner,
            },
          },
        );
        return false;
      }
      // (B) only end a session that is actually live.
      if (data.broadcastingInfo?.isLive !== true) {
        appLogger.error(
          'Refusing to end live: message is not live',
          'FirestoreService.endCommunityLiveMessage',
          { context: { messageId } },
        );
        return false;
      }

      const notificationRef = doc(
        db,
        'users',
        notificationUserId,
        'notifications',
        notificationId,
      );

      return await runTransaction(db, async (transaction) => {
        transaction.set(messageRef, messageUpdate, { merge: true });
        transaction.set(notificationRef, notificationData, { merge: true });
        return true;
      });
    } catch (error) {
      appLogger.error(
        'Error ending community live message',
        'FirestoreService.endCommunityLiveMessage',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Lightweight, unload-safe variant of `endCommunityLiveMessage` that flips the
   * live message's `broadcastingInfo.isLive` to `false` with a SINGLE direct
   * `setDoc(..., { merge: true })` — no `runTransaction`, no server-side read and
   * no `collectionGroup` pre-query.
   *
   * WHY A SECOND METHOD (web-specific divergence). When the broadcaster kills the
   * browser tab, `beforeunload` fires the end-live write as fire-and-forget. The
   * transactional `endCommunityLiveMessage` above is a multi-round-trip
   * read-then-commit preceded by a collection-group lookup; the browser tears the
   * page down before it can land, so the message stays `isLive: true` forever
   * (finding_5). This method issues the same lightweight single-write shape as the
   * watcher decrement (`setCommunityLiveWatchers`, :2049) that DOES reliably flush
   * during unload. Flutter (mobile) disposes via the app lifecycle where the
   * transaction completes normally, so this is a web-only reliability path for the
   * tab-kill/unload case; the in-session (SPA-nav) end keeps the transactional
   * `endCommunityLiveMessage` above. Do not change Flutter.
   *
   * Because the broadcaster is (by definition) the wall `owner`, the live message
   * doc path is known directly and needs no lookup:
   *   users/{owner}/community_messages/{messageId}
   * — Flutter writes community message docs at exactly that path
   *   (`user.collection('community_messages').doc(message.id)`), so the doc id IS
   *   the message's `id` field.
   *
   * Write (parity with the transactional path's message write and Flutter's
   * `messageUpdateInfo`): the `messageUpdate` payload — `{ text, updatedTimestamp,
   * broadcastingInfo: { isLive: false } }` — is serialized in the domain
   * (`LiveStreamingService`) and passed opaquely to keep the data-layer surface
   * JSON-only (data-layer.md surface rule), matching the identical `messageUpdate`
   * argument on `endCommunityLiveMessage` above.
   *
   * Owner guard: verified client-side against `auth.currentUser` (in-memory,
   * synchronous — available during `beforeunload` and adds NO server round-trip),
   * so a caller cannot flip an arbitrary owner's message. The transactional path's
   * `isLive === true` pre-check is intentionally DROPPED here: it would require the
   * very server read this method exists to avoid, and re-writing `isLive: false`
   * on an already-ended message is idempotent/harmless.
   *
   * The `liveEnded` notification write (the transactional path's second write) is
   * intentionally NOT part of this critical-path method — on a hard tab-kill the
   * only write that must land is `isLive: false`; the notification is secondary
   * (finding_5 step 2). Callers that still want it fire it as a separate,
   * non-blocking write.
   *
   * Matches Flutter message-write payload: lib/data/data_sources/
   * community_data_source.dart:198-245 (`endLiveMessage`, :220-228 messageUpdate);
   * doc path: :135-139 (`addCommunityMessage`,
   * `users/{owner}/community_messages/{message.id}`).
   *
   * @param owner - The broadcaster's firebaseId (== wall owner == the message
   *   doc's parent user). Must equal the current auth uid.
   * @param messageId - The live message's `id` field (== its Firestore doc id).
   * @param messageUpdate - The serialized merge payload for the message doc
   *   (`{ text, updatedTimestamp, broadcastingInfo: { isLive: false } }`).
   * @returns Promise<boolean> - true on success, false otherwise (parity with the
   *   transactional path's `bool success` pattern).
   */

  // NOTE: When Firestore writes will be replaced by Cloud functions, this method will be removed 
  // and the transactional path will be the only way to end a live session. The transactional path 
  // will be called by the Cloud function, which will be triggered by the broadcaster's action to end the live session.

  async endCommunityLiveMessageByOwner(
    owner: string,
    messageId: string,
    messageUpdate: JsonObject,
  ): Promise<boolean> {
    try {
      const currentFirebaseId = auth.currentUser?.uid;
      // Only the broadcaster (== wall owner) may end their own session. Checked
      // in-memory (no server read) so it stays flush-friendly during unload.
      if (!currentFirebaseId || currentFirebaseId !== owner) {
        appLogger.error(
          'Refusing to end live (by owner): current user is not the broadcaster',
          'FirestoreService.endCommunityLiveMessageByOwner',
          { context: { messageId, currentFirebaseId, owner } },
        );
        return false;
      }

      const messageRef = doc(
        db,
        'users',
        owner,
        'community_messages',
        messageId,
      );
      await setDoc(messageRef, messageUpdate, { merge: true });
      return true;
    } catch (error) {
      appLogger.error(
        'Error ending community live message by owner',
        'FirestoreService.endCommunityLiveMessageByOwner',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Increment (join) or decrement (leave) the watcher count on the single live
   * community message identified by `liveStreamingMessageId`.
   *
   * The target message reference is resolved via the same collection-group
   * lookup as `likeCommunityMessage` (:1740-1745):
   *   collectionGroup('community_messages')
   *     .where('id', '==', liveStreamingMessageId)
   *     .orderBy('createdTimestamp', 'desc')
   *     .limit(1)                              ->  docs[0].ref
   *
   * Write (parity with Flutter's nested-map merge):
   *   set(ref, { broadcastingInfo: { watchers: increment(joined ? 1 : -1) } },
   *       { merge: true })
   *
   * IMPORTANT: the `increment` sits nested UNDER the `broadcastingInfo` map key
   * (a nested-map merge), NOT a dotted `broadcastingInfo.watchers` field path.
   * Flutter uses the nested-map form and `SetOptions(merge: true)` preserves the
   * sibling `broadcastingInfo` fields (`likes`, `coins`, `isLive`, ...) —
   * mirroring the exact same shape as its `sendLiveLike` sibling.
   *
   * Matches Flutter: lib/data/data_sources/live_streaming_data_source.dart:32-59
   * (`LiveStreamingDataSource.setLiveWatchers`):
   *   - :35-40 collectionGroup('community_messages').where('id', ==)
   *       .orderBy('createdTimestamp', desc).limit(1) -> docs.first.reference
   *   - :42-52 broadcastMessageReference.set(
   *       { 'broadcastingInfo': { 'watchers': FieldValue.increment(joined ? 1 : -1) } },
   *       SetOptions(merge: true))
   *   - :53-56 returns false on catch
   *
   * The nested collection-group + orderBy combination relies on a Firestore
   * composite index; the Flutter app already issues this exact query and web
   * `likeCommunityMessage` (:1740-1745) already relies on it in the shared
   * project, so no web-side index change is authored here.
   *
   * Divergence (defensive only, matching `likeCommunityMessage`): the lookup +
   * empty-result guard sit inside the same try/catch, so any failure surfaces as
   * `false` rather than throwing on `docs.first` (Flutter's `docs.first` at :50
   * would throw on an empty result, still caught by its `try` at :34).
   *
   * @param liveStreamingMessageId - The `id` field of the live message whose
   *   watcher count is updated (used to locate the doc via the collection-group
   *   query).
   * @param joined - true increments the count (+1) on join, false decrements
   *   (-1) on leave.
   * @returns Promise<boolean> - true on success, false otherwise (parity with
   *   Flutter's `bool` pattern at `live_streaming_data_source.dart:53-58`).
   */
  async setCommunityLiveWatchers(
    liveStreamingMessageId: string,
    joined: boolean,
  ): Promise<boolean> {
    try {
      const messageSnapshot = await getDocs(
        query(
          collectionGroup(db, 'community_messages'),
          where('id', '==', liveStreamingMessageId),
          orderBy('createdTimestamp', 'desc'),
          limit(1),
        ),
      );
      if (messageSnapshot.empty) {
        appLogger.error(
          'Community live message not found while setting watchers',
          'FirestoreService.setCommunityLiveWatchers',
          { context: { liveStreamingMessageId } },
        );
        return false;
      }
      const messageDoc = messageSnapshot.docs[0];
      const messageRef = messageDoc.ref;

      const data = messageDoc.data() as {
        broadcastingInfo?: { isLive?: boolean };
      };
      // Only bump the watcher count on a session that is actually live, so a
      // session that has already ended (or a non-live message) is left alone.
      if (data.broadcastingInfo?.isLive !== true) {
        appLogger.error(
          'Refusing to set watchers: message is not live',
          'FirestoreService.setCommunityLiveWatchers',
          { context: { liveStreamingMessageId, joined } },
        );
        return false;
      }

      await setDoc(
        messageRef,
        { broadcastingInfo: { watchers: increment(joined ? 1 : -1) } },
        { merge: true },
      );
      return true;
    } catch (error) {
      appLogger.error(
        'Error setting community live watchers',
        'FirestoreService.setCommunityLiveWatchers',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Subscribe to the single live community message identified by
   * `liveStreamingMessageId`, surfacing every doc change so the consumer can
   * track the live session's `broadcastingInfo` (watcher count) and detect an
   * in-session end (`isLive` flips to false).
   *
   * Query (same collection-group lookup as `likeCommunityMessage`, :1740-1745):
   *   collectionGroup('community_messages')
   *     .where('id', '==', liveStreamingMessageId)
   *     .orderBy('createdTimestamp', 'desc')
   *     .limit(1)
   *     .onSnapshot(...)
   *
   * Mirrors Flutter's `subscribeToBroadcastingMessageStats`
   * (lib/presentation/screens/community/live_streaming_screen.dart:704-711):
   *   FirebaseFirestore.instance
   *     .collectionGroup('community_messages')
   *     .where('id', isEqualTo: widget.liveStreamingMessageId)
   *     .orderBy('createdTimestamp', descending: true)
   *     .limit(1)
   *     .snapshots();
   *
   * Flutter `continue`s on `DocumentChangeType.removed` (:716-718) and gates
   * `modified`-vs-`added` in its listener. That change-type gating (skip
   * `removed`, offline detection on `!isLive`) is the consumer's concern and
   * lands in the Task-8 use case — this method stays a thin data proxy and
   * surfaces ALL change types (incl. `removed`) as raw `JsonObject` so the
   * domain `CommunityChatMessageRemapper.fromJson` can build the entity. The
   * stored doc already carries its own `id` field (the field this query filters
   * on), so it is spread through as-is (matching `subscribeToCommunityMessages`,
   * :1573-1576) rather than re-injecting `doc.id`.
   *
   * Reuses the same composite index as `likeCommunityMessage` (:1740-1745) — no
   * new web-side index is authored here.
   *
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToCommunityLiveMessage(
    liveStreamingMessageId: string,
    onChange: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collectionGroup(db, 'community_messages'),
      where('id', '==', liveStreamingMessageId),
      orderBy('createdTimestamp', 'desc'),
      limit(1),
    );
    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: { ...c.doc.data() },
        }));
        if (changes.length > 0) onChange(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Get a page of notifications for `owner` from the `notifications`
   * collection-group, newest-first.
   *
   * CollectionGroup: notifications
   * Filter:   where('info.usersInfo.peerUserId', '==', owner)
   * Ordering: orderBy('info.timestamp', 'desc')
   * Cursor:   PageCursor (startAfter the last doc of the previous page)
   *
   * Mirrors Flutter `MessagesDataSource.getNotifications`
   * (lib/data/data_sources/messages_data_source.dart:7-44):
   *   FirebaseFirestore.instance
   *     .collectionGroup('notifications')
   *     .where("info.usersInfo.peerUserId", isEqualTo: owner)
   *     .orderBy("info.timestamp", descending: true)
   *     .limit(limit)               // + startAfterDocument(lastFetchedMedia)
   *   hasMore = querySnapshot.docs.length == limit;   // line 35
   *
   * The nested filter field is literally `info.usersInfo.peerUserId` — the
   * Flutter `UsersInfo`/`NotificationInfo`/`AppNotification` models carry no
   * `@JsonKey` overrides, so each Dart property name equals its Firestore field
   * (lib/data/models/users_info.dart:6-16).
   *
   * Returns raw `JsonObject`s (doc id spread in as `id`) so the domain
   * `AppNotificationRemapper.fromDto(fromJson(...))` bridge can build the entity.
   * The `PageCursor` stays opaque (a Firestore `QueryDocumentSnapshot`
   * internally) and does not leak past this service.
   */
  async getNotifications(
    owner: string,
    cursor: PageCursor | undefined,
    limitCount: number,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const notificationsRef = collectionGroup(db, 'notifications');

      let q = query(
        notificationsRef,
        where('info.usersInfo.peerUserId', '==', owner),
        orderBy('info.timestamp', 'desc'),
        limit(limitCount),
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching notifications', 'FirestoreService.getNotifications', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get a page of users' in-app feedback from the `in_app_feedback`
   * collection group, newest-first.
   *
   * Collection group: in_app_feedback
   * Ordering:         orderBy('timestamp', 'desc')
   * Cursor:           document-cursor startAfter(lastDoc)
   *
   * Mirrors Flutter's FeedbackDataSource.getUsersFeedback in
   * lib/data/data_sources/feedback_data_source.dart:21-55:
   *   FirebaseFirestore.instance
   *     .collectionGroup('in_app_feedback')
   *     .orderBy("timestamp", descending: true)
   *     .startAfterDocument(lastFetchedItem)   // only when paging
   *     .limit(limit)
   *
   * Returns the raw `doc.data()` JsonObjects WITHOUT injecting the Firestore
   * doc id — unlike getNotifications. Flutter parses
   * `InAppFeedback.fromJson(json.data())` reading the document's own stored
   * `id` field (in_app_feedback.g.dart:10), while the Firestore doc id is the
   * unrelated `${userId}_${timestamp}` composite
   * (feedback_data_source.dart:11). Spreading `id: doc.id` would overwrite the
   * authoritative stored `id` and diverge from Flutter.
   *
   * The `PageCursor` stays opaque (a Firestore `QueryDocumentSnapshot`
   * internally) and does not leak past this service.
   *
   * Authorization gate (mirrors the future Cloud Function): this is the real
   * enforcement point for reading other users' feedback. The client-side
   * `useAccountInfoItems` `isAdmin` selector only hides the Admin entry in the
   * account-info sheet — a non-admin could still navigate to the route or call
   * this path directly. So before running the query we self-resolve the
   * authenticated caller via `auth.currentUser` and confirm their stored user
   * `type` is admin (2) or super-admin (3, see domain/enums/userType.ts).
   * Any non-admin (or unauthenticated) caller short-circuits to the empty
   * result `{ items: [], hasMore: false }` without touching the collection
   * group. This is a deliberate web-side hardening that is stricter than the
   * current Flutter `FeedbackDataSource.getUsersFeedback`, which gates only on
   * a non-null Firebase user; the same admin check will live in the eventual
   * Cloud Function.
   */
  async getUsersFeedback(
    cursor: PageCursor | undefined,
    limitCount: number,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      // Authorization gate (see method JSDoc): only admin / super-admin
      // callers may read other users' feedback. Self-resolve the caller from
      // the auth state; the future Cloud Function will resolve it from the
      // verified auth context the same way.
      const currentFirebaseId = auth.currentUser?.uid;
      if (!currentFirebaseId) {
        return { items: [], hasMore: false };
      }
      const currentUser = await this.getUser(currentFirebaseId);
      // getUser returns { id, ...data }, so `type` is the stored user field.
      const userType = currentUser?.type as number | undefined;
      // UserType.admin = 2, UserType.superAdmin = 3 (domain/enums/userType.ts).
      if (userType !== 2 && userType !== 3) {
        return { items: [], hasMore: false };
      }

      const feedbackRef = collectionGroup(db, 'in_app_feedback');

      let q = query(
        feedbackRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount),
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(d => d.data() as JsonObject);

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching users feedback', 'FirestoreService.getUsersFeedback', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Save an in-app feedback document to the top-level `in_app_feedback`
   * collection.
   *
   * Path:  /in_app_feedback/{userId}_{millis}
   * Write: set(feedbackData, { merge: true })
   *
   * Faithful port of Flutter's `FeedbackDataSource.saveFeedback(InAppFeedback)`
   * (lib/data/data_sources/feedback_data_source.dart:7-19):
   *   FirebaseFirestore.instance
   *     .collection('in_app_feedback')
   *     .doc("${feedback.userId}_${DateTime.now().millisecondsSinceEpoch}")
   *     .set(feedback.toJson(), SetOptions(merge: true));
   *
   * The write targets the TOP-LEVEL `in_app_feedback` collection (Flutter's
   * `.collection(...)`), whereas the admin read (`getUsersFeedback`) queries the
   * `in_app_feedback` collection GROUP — they intentionally use the same name at
   * two levels, matching Flutter.
   *
   * The doc id is the `${userId}_${millis}` composite Flutter builds in its data
   * source from a FRESH `DateTime.now()` — distinct from the payload's own
   * `timestamp` field (which the domain service stamps a moment earlier, exactly
   * as Flutter's repository does at feedback_repository.dart:50 vs the data
   * source's second `DateTime.now()` at feedback_data_source.dart:11). Building
   * the id here (not in the domain) keeps this method a 1:1 port of Flutter's
   * data source — the `Date.now()` below is that second, distinct call.
   *
   * The serialized payload is built by the domain service (an `InAppFeedback`
   * `toJson`-equivalent); this data-layer method takes an opaque `JsonObject` to
   * keep the data-layer surface JSON-only (data-layer.md surface rule). The
   * doc-id prefix `userId` is read out of that payload (`feedback['userId']`),
   * exactly as Flutter reads `feedback.userId` at feedback_data_source.dart:11.
   *
   * @param feedback - The serialized `InAppFeedback` payload (carries `userId`).
   * @returns Promise<boolean> - true on success, false otherwise (parity with
   *   Flutter's `bool` return at feedback_data_source.dart:10/18).
   */
  async saveFeedback(feedback: JsonObject): Promise<boolean> {
    try {
      const userId = feedback['userId'] as string;
      const docId = `${userId}_${Date.now()}`;
      await setDoc(doc(db, 'in_app_feedback', docId), feedback, { merge: true });
      return true;
    } catch (error) {
      appLogger.error(
        'Error saving feedback',
        'FirestoreService.saveFeedback',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Get a page of chat nodes (direct-message conversations) for `userId` from
   * the `message_nodes` collection, newest-first.
   *
   * Collection: message_nodes
   * Filters:    where('users', 'array-contains', userId)
   *             where('blocked', '==', false)
   * Ordering:   orderBy('timestamp', 'desc')
   * Cursor:     value-cursor startAfter(lastTimestamp)
   *
   * Mirrors Flutter's chat-list page query in
   * lib/presentation/screens/messages/messages_screen.dart:251-265:
   *   FirebaseFirestore.instance
   *     .collection('message_nodes')
   *     .where("users", arrayContains: _myUser.firebaseId)
   *     .where("blocked", isEqualTo: false)
   *     .orderBy('timestamp', descending: true)
   *     .limit(_chatDocumentLimit)   // + startAfterDocument(_lastFetchedChatDocument)
   *
   * The `timestamp` field carries no `@JsonKey` override on
   * `ChatMessageNode` (lib/data/models/chat_message_node.dart:40), so the
   * Firestore field is literally `timestamp`.
   *
   * Returns raw `JsonObject`s (doc id spread in as `id` so `documentId`
   * survives for the remapper) and `lastTimestamp` (the timestamp of the last
   * doc, or `null` if the page is empty). `hasMore` derivation is the caller's
   * responsibility (compare returned length to `limit`) — kept out of this
   * layer, mirroring `fetchChatMessagesPage`.
   */
  async getChatNodesPage(
    userId: string,
    lastTimestamp: number | null,
    limitCount: number,
  ): Promise<{ nodes: JsonObject[]; lastTimestamp: number | null }> {
    try {
      const nodesRef = collection(db, 'message_nodes');

      let q = query(
        nodesRef,
        where('users', 'array-contains', userId),
        where('blocked', '==', false),
        orderBy('timestamp', 'desc'),
        limit(limitCount),
      );

      if (lastTimestamp !== null) {
        q = query(q, startAfter(lastTimestamp));
      }

      const snapshot = await getDocs(q);
      const nodes: JsonObject[] = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      const tail = snapshot.docs[snapshot.docs.length - 1];
      const nextLastTimestamp = tail
        ? ((tail.data() as { timestamp?: number }).timestamp ?? null)
        : null;

      return { nodes, lastTimestamp: nextLastTimestamp };
    } catch (error) {
      appLogger.error(
        'Error fetching chat nodes page',
        'FirestoreService.getChatNodesPage',
        { error: error as Error },
      );
      // Surface to caller — the inbox chat hook needs to distinguish first-load
      // failure (show error state) from an empty page (show empty state).
      throw error;
    }
  }

  /**
   * Subscribe to live chat-node changes for `userId`'s direct-message list.
   *
   * Collection: message_nodes
   * Filters:    where('users', 'array-contains', userId)
   *             where('blocked', '==', false)
   *   plus a CONDITIONAL timestamp filter (see below).
   *
   * Mirrors Flutter's `_subscribeToChatUpdates`
   * (lib/presentation/screens/messages/messages_screen.dart:459-476). The
   * `timestamp` filter is conditional on whether the first page was non-empty:
   * - When `sinceTimestamp` is a number (first page had documents, so Flutter's
   *   `_mostRecentChatDocument != null`), ADD
   *   `where('timestamp', '>=', sinceTimestamp)` — Flutter uses
   *   `isGreaterThanOrEqualTo` (lines 469-472), i.e. `>=`, NOT `>`. This
   *   differs from the per-chat message subscription (`subscribeToChatMessages`)
   *   which uses `>`.
   * - When `sinceTimestamp` is `null`/`undefined` (the first page was EMPTY, so
   *   `_mostRecentChatDocument == null`), OMIT the timestamp filter entirely
   *   (Flutter `query.snapshots()`, lines 474-475) so brand-new conversations
   *   are caught. There is deliberately NO `0`/`Date.now()` fallback.
   *
   * Emits the raw doc data (with the document id spread in as `id`) so the
   * domain layer's `ChatMessageNodeRemapper.fromDto(fromJson(...))` bridge can
   * build the entity.
   *
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToChatNodes(
    userId: string,
    sinceTimestamp: number | null | undefined,
    onChange: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    let q = query(
      collection(db, 'message_nodes'),
      where('users', 'array-contains', userId),
      where('blocked', '==', false),
    );

    if (typeof sinceTimestamp === 'number') {
      q = query(q, where('timestamp', '>=', sinceTimestamp));
    }

    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: { id: c.doc.id, ...c.doc.data() },
        }));
        if (changes.length > 0) onChange(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Subscribe to live `notifications` collection-group changes for `userId`.
   *
   * CollectionGroup: notifications
   * Filters:    where('info.usersInfo.peerUserId', '==', userId)
   *             where('info.timestamp', '>=', sinceTimestamp)
   * Ordering:   NONE — the live query has no `orderBy`; the desc re-sort by
   *             `info.timestamp` is the consumer's job (the only ordered read
   *             is the page fetch, `getNotifications` above).
   *
   * Mirrors Flutter's `_subscribeToNotificationsUpdates`
   * (lib/presentation/screens/messages/messages_screen.dart:323-337):
   *   FirebaseFirestore.instance
   *     .collectionGroup('notifications')
   *     .where("info.usersInfo.peerUserId", isEqualTo: _myUser.firebaseId)
   *     .where("info.timestamp", isGreaterThanOrEqualTo: timestampObservation)
   *     .snapshots()
   *
   * The `info.timestamp` cursor is `>=` (Flutter `isGreaterThanOrEqualTo`,
   * line 333-336), NOT `>` — this differs from the per-chat-message
   * subscription (`subscribeToChatMessages`) which uses `>`.
   *
   * `sinceTimestamp` is always a resolved number here: the caller (the
   * presentation hook, Task 4) makes the `now()`-vs-most-recent-notification
   * decision (`messages_screen.dart:324-327`,
   * `_mostRecentNotificationsDocument == null ? DateTime.now()... : ...`) before
   * invoking this method. Unlike chat, there is no empty-first-page branch that
   * omits the timestamp filter — the notifications subscription always filters.
   *
   * The nested filter field is literally `info.usersInfo.peerUserId` and the
   * cursor field literally `info.timestamp` — the Flutter `UsersInfo` /
   * `NotificationInfo` models carry no `@JsonKey` overrides, so each Dart
   * property name equals its Firestore field (lib/data/models/users_info.dart:8,
   * lib/data/models/notification_info.dart:35).
   *
   * Emits the raw doc data (with the document id spread in as `id`) so the
   * domain layer's `AppNotificationRemapper.fromDto(fromJson(...))` bridge can
   * build the entity (the `id` backs `AppNotificationEntity.id`, used for the
   * consumer's identity/dedup keying).
   *
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToNotifications(
    userId: string,
    sinceTimestamp: number,
    onChange: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collectionGroup(db, 'notifications'),
      where('info.usersInfo.peerUserId', '==', userId),
      where('info.timestamp', '>=', sinceTimestamp),
    );

    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: { id: c.doc.id, ...c.doc.data() },
        }));
        if (changes.length > 0) onChange(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Mark a peer message as seen, flipping `seenByPeer: true` on BOTH the
   * individual message doc and the chat-node metadata doc in a single
   * transaction.
   *
   * Writes to:
   * - /message_nodes/{groupChatId}/messages/{messageId} - the message doc
   * - /message_nodes/{groupChatId}                     - the chat-node metadata
   *
   * Matches Flutter: lib/data/data_sources/chat_data_source.dart:100-126
   * (`ChatDataSource.setPeerMessageSeen`). The Flutter side guards on
   * `chatMessage.seenByPeer == false` at the call site — that no-op guard
   * lives at the use-case layer on web (see `MarkPeerMessageSeenUseCase`).
   * This data-layer method ALWAYS issues the transaction.
   *
   * @param groupChatId - The chat's group id (sorted-hashCode join of the two user ids).
   * @param messageId - The message document id (the `documentId` on `ChatMessage`).
   */
  async setPeerMessageSeen(groupChatId: string, messageId: string): Promise<void> {
    try {
      const chatNodeRef = doc(db, 'message_nodes', groupChatId);
      const messageRef = doc(db, 'message_nodes', groupChatId, 'messages', messageId);

      await runTransaction(db, async (transaction) => {
        transaction.update(messageRef, { seenByPeer: true });
        transaction.update(chatNodeRef, { seenByPeer: true });
      });
    } catch (error) {
      appLogger.error(
        'Error setting peer message seen',
        'FirestoreService.setPeerMessageSeen',
        { error: error as Error },
      );
      throw error;
    }
  }

  /**
   * Save a single chat message + its chat-node metadata in one Firestore
   * transaction.
   *
   * Writes to:
   * - /message_nodes/{groupChatId}/messages/{messageId} - the message doc
   * - /message_nodes/{groupChatId}                     - the chat-node metadata (merged)
   *
   * Matches Flutter: lib/data/data_sources/chat_data_source.dart:7-49
   * (`ChatDataSource.saveChatMessage`). Structurally mirrors the per-message
   * body inside `saveBatchChatMessages` above (the inner block at the loop
   * body, NOT the outer `writeBatch` wrapper) — same two doc refs, same
   * payloads — but issues a single `runTransaction` for one message rather
   * than a batched write across many.
   *
   * The metadata payload is built by the domain service (`ChatService.saveChatMessage`)
   * — this data-layer method takes opaque `JsonObject` payloads to keep the
   * data-layer surface JSON-only (data-layer.md surface rule).
   *
   * @param groupChatId - The chat's group id (sorted-hashCode join of the two user ids).
   * @param messageId - The message document id (the `documentId` on `ChatMessage`).
   * @param messageData - The serialized message payload (output of `ChatMessageRemapper.toJson`).
   * @param metadataData - The chat-node metadata payload (see `chat_data_source.dart:24-42` for the field set).
   * @returns Promise<boolean> - true if successful, false otherwise (parity with Flutter's `bool result` pattern at `chat_data_source.dart:9, 45`).
   */
  async saveChatMessage(
    groupChatId: string,
    messageId: string,
    messageData: JsonObject,
    metadataData: JsonObject,
  ): Promise<boolean> {
    try {
      const chatNodeRef = doc(db, 'message_nodes', groupChatId);
      const messageRef = doc(db, 'message_nodes', groupChatId, 'messages', messageId);

      await runTransaction(db, async (transaction) => {
        transaction.set(messageRef, messageData, { merge: true });
        transaction.set(chatNodeRef, metadataData, { merge: true });
      });

      return true;
    } catch (error) {
      appLogger.error(
        'Error saving single chat message',
        'FirestoreService.saveChatMessage',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Save or remove a user from favorites
   * Path: /users/{currentUserId}/savedUsers/{savedUserFirebaseId}
   *
   * Matches Flutter implementation: UsersDataSource.setUserIsFavourite
   * Source: lib/data/datasource/users_data_source.dart
   *
   * @param currentUserId - Current authenticated user's Firebase ID
   * @param savedUserId - Firebase ID of the user to save/remove
   * @param shouldSave - true to save, false to remove
   * @param createdDate - Timestamp when saved (only used when shouldSave is true)
   */
  async saveUserToFavorites(
    currentUserId: string,
    savedUserId: string,
    shouldSave: boolean,
    createdDate: number
  ): Promise<boolean> {
    try {
      const savedUserRef = doc(db, 'users', currentUserId, 'savedUsers', savedUserId);

      if (shouldSave) {
        // Save the user to favorites
        await setDoc(savedUserRef, {
          firebaseId: savedUserId,
          createdDate: createdDate,
        });
      } else {
        // Remove the user from favorites
        await deleteDoc(savedUserRef);
      }

      return true;
    } catch (error) {
      appLogger.error('Error saving user to favorites', 'FirestoreService.saveUserToFavorites', { error: error as Error });
      return false;
    }
  }

  /**
   * Mirrors Flutter's UsersDataSource.getSavedUsers
   * (lib/data/data_sources/users_data_source.dart:225-251).
   *
   * Fetches user documents whose `firebase_id` matches any value in
   * `firebaseIds` via a `collectionGroup('users')` query ordered by `name`
   * descending; `firebaseIds` is assumed to already respect Firestore's
   * `FIRESTORE_WHERE_IN_MAX_VALUES` (10) cap. Callers MUST chunk upstream —
   * `userService.fetchUsersByIds` (the sole caller) does NOT chunk and
   * forwards its input verbatim, so the use-case layer is responsible for
   * staying within the cap (see `fetchSavedUsersPageUseCase`, whose page
   * size defaults to `FIRESTORE_WHERE_IN_MAX_VALUES`).
   *
   * Returns `[]` on empty input (avoids issuing an invalid `in []` query) and
   * on caught error (parity with Flutter's `print` + empty response on catch).
   *
   * Blocked-user filtering is intentionally NOT performed here — a deliberate
   * divergence from Flutter, which post-filters in the data source. The web's
   * canonical predicate (`shouldFilterOutBlockedUserUseCase`) lives in the
   * domain layer and is applied by `userService.fetchUsersByIds`.
   */
  async getUsersByFirebaseIds(firebaseIds: string[]): Promise<JsonObject[]> {
    if (firebaseIds.length === 0) {
      return [];
    }

    try {
      const usersRef = collectionGroup(db, 'users');
      const q = query(
        usersRef,
        where('firebase_id', 'in', firebaseIds),
        orderBy('name', 'desc'),
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => doc.data());
    } catch (error) {
      appLogger.error('Error fetching users by firebase ids', 'FirestoreService.getUsersByFirebaseIds', { context: { firebaseIds }, error: error as Error });
      return [];
    }
  }

  // ============================================
  // CONTENT METHODS
  // ============================================

  /**
   * Link two of the current user's media items together via a transaction.
   * Mirrors Flutter's ContentDataSource.linkContent
   * (lib/data/data_sources/content_data_source.dart:557).
   * Each side stores the other's `relativePath` in `contentLink`.
   */
  async linkContent(
    userId: string,
    sourceMediaId: string,
    sourceRelativePath: string,
    destinationMediaId: string,
    destinationRelativePath: string,
  ): Promise<boolean> {
    try {
      const sourceRef = doc(db, 'users', userId, 'media', sourceMediaId);
      const destinationRef = doc(db, 'users', userId, 'media', destinationMediaId);
      const updatedTimestamp = Date.now();
      await runTransaction(db, async (transaction) => {
        transaction.set(
          sourceRef,
          { contentLink: destinationRelativePath, updatedTimestamp },
          { merge: true },
        );
        transaction.set(
          destinationRef,
          { contentLink: sourceRelativePath, updatedTimestamp },
          { merge: true },
        );
      });
      return true;
    } catch (error) {
      appLogger.error('Error linkContent', 'FirestoreService.linkContent', { error: error as Error });
      return false;
    }
  }

  /**
   * Unlink the source media from its peer.
   * Mirrors Flutter's ContentDataSource.unlinkContent
   * (lib/data/data_sources/content_data_source.dart:593).
   * The peer is found by querying media where `path == sourceMedia.contentLink`.
   * Returns the peer media id on success (so callers can update both sides),
   * or null on failure.
   */
  async unlinkContent(
    userId: string,
    sourceMediaId: string,
    sourceContentLink: string,
  ): Promise<string | null> {
    try {
      const sourceRef = doc(db, 'users', userId, 'media', sourceMediaId);

      const peerQuery = query(
        collection(db, 'users', userId, 'media'),
        where('path', '==', sourceContentLink),
        orderBy('created_timestamp', 'desc'),
        limit(1),
      );
      const peerSnapshot = await getDocs(peerQuery);
      if (peerSnapshot.empty) {
        appLogger.warning('unlinkContent: peer not found', 'FirestoreService.unlinkContent');
        return null;
      }
      const peerDoc = peerSnapshot.docs[0];
      const peerRef = peerDoc.ref;

      await runTransaction(db, async (transaction) => {
        transaction.set(sourceRef, { contentLink: null }, { merge: true });
        transaction.set(peerRef, { contentLink: null }, { merge: true });
      });
      return peerDoc.id;
    } catch (error) {
      appLogger.error('Error unlinkContent', 'FirestoreService.unlinkContent', { error: error as Error });
      return null;
    }
  }

  /**
   * Save or remove a content item from current user's watch later list
   * Matches Flutter's ContentDataSource.saveContent pattern
   * Collection: users/{currentUserId}/savedContent/{dto.id}
   *
   * @param currentUserId - Current authenticated user's Firebase ID
   * @param dto - The saved content DTO
   * @param shouldSave - true to save, false to remove
   */
  async saveContent(
    currentUserId: string,
    dto: SavedContentDto,
    shouldSave: boolean
  ): Promise<boolean> {
    try {
      const savedContentRef = doc(db, 'users', currentUserId, 'savedContent', dto.id);

      if (shouldSave) {
        await setDoc(savedContentRef, {
          id: dto.id,
          firebaseId: dto.firebaseId,
          thumbnail: dto.thumbnail,
          path: dto.path,
          createdDate: dto.createdDate,
        });
      } else {
        await deleteDoc(savedContentRef);
      }

      return true;
    } catch (error) {
      appLogger.error('Error saving content to watch later', 'FirestoreService.saveContent', { error: error as Error });
      return false;
    }
  }

  /**
   * Get user report items from Firestore
   * Collection: user_report_items
   *
   * Matches Flutter's getUserReportItems from report_data_source.dart
   *
   * @param parentId - null for root items, UUID string for children
   * @returns List of raw report item documents ordered by 'order' field
   */
  async getUserReportItems(parentId: string | null): Promise<JsonObject[]> {
    try {
      const col = collection(db, 'user_report_items');

      let q;
      if (parentId !== null) {
        q = query(col, where('parentId', '==', parentId), orderBy('order'));
      } else {
        q = query(col, where('parentId', '==', null), orderBy('order'));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      appLogger.error('Error fetching user report items', 'FirestoreService.getUserReportItems', { error: error as Error });
      throw error;
    }
  }

  // ============================================
  // WALLET TRANSACTION METHODS
  // ============================================

  /**
   * Get the current user's wallet transactions (paginated, owner-scoped).
   * CollectionGroup: transactions (subcollection under each user).
   * Flutter source: lib/data/data_sources/wallet_data_source.dart (getWalletTransactions, !isFromAdmin branch).
   *
   * Always filters by `usersInfo.usersIds array-contains userId`. The admin
   * "all users" variant is a SEPARATE function, `getAdminWalletTransactions`,
   * so there is no `isFromAdmin` flag on this path. (Web-only divergence from
   * Flutter, which keeps one flagged function — chosen so each path maps to its
   * own Cloud Function later.)
   */
  async getWalletTransactions(
    userId: string,
    cursor?: PageCursor,
    limitCount: number = 20
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const transactionsRef = collectionGroup(db, 'transactions');

      const constraints: QueryConstraint[] = [
        where('usersInfo.usersIds', 'array-contains', userId),
        orderBy('updatedDate', 'desc'),
        limit(limitCount),
      ];

      let q = query(transactionsRef, ...constraints);

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(d => d.data());

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching wallet transactions', 'FirestoreService.getWalletTransactions', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get ALL users' wallet transactions (paginated) — admin variant.
   * CollectionGroup: transactions, with NO owner filter.
   * Flutter source: lib/data/data_sources/wallet_data_source.dart (getWalletTransactions, isFromAdmin branch).
   *
   * Separate from `getWalletTransactions` on purpose: no `isFromAdmin` flag on
   * the wire. This will become its own Cloud Function with server-side admin
   * authorization; until then the only gate is which use case the hook calls.
   */
  async getAdminWalletTransactions(
    cursor?: PageCursor,
    limitCount: number = 20
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const transactionsRef = collectionGroup(db, 'transactions');

      const constraints: QueryConstraint[] = [
        orderBy('updatedDate', 'desc'),
        limit(limitCount),
      ];

      let q = query(transactionsRef, ...constraints);

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(d => d.data());

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching admin wallet transactions', 'FirestoreService.getAdminWalletTransactions', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Refresh wallet transactions (fetch newer ones)
   * Flutter source: lib/data/data_sources/wallet_data_source.dart - refreshWalletTransactions
   *
   * Fetches transactions created after the given timestamp.
   */
  async refreshWalletTransactions(
    userId: string,
    afterTimestamp: number,
    limitCount: number = 20
  ): Promise<{ items: JsonObject[]; hasMore: boolean }> {
    try {
      const transactionsRef = collectionGroup(db, 'transactions');

      const q = query(
        transactionsRef,
        where('usersInfo.usersIds', 'array-contains', userId),
        where('updatedDate', '>', afterTimestamp),
        orderBy('updatedDate', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(d => d.data());

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
      };
    } catch (error) {
      appLogger.error('Error refreshing wallet transactions', 'FirestoreService.refreshWalletTransactions', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get content report items from Firestore
   * Collection: content_report_items
   *
   * Matches Flutter's getContentReportItems from report_data_source.dart
   *
   * @param parentId - null for root items, UUID string for children
   * @returns List of raw report item documents ordered by 'order' field
   */
  async getContentReportItems(parentId: string | null): Promise<JsonObject[]> {
    try {
      const col = collection(db, 'content_report_items');

      let q;
      if (parentId !== null) {
        q = query(col, where('parentId', '==', parentId), orderBy('order'));
      } else {
        q = query(col, where('parentId', '==', null), orderBy('order'));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      appLogger.error('Error fetching content report items', 'FirestoreService.getContentReportItems', { error: error as Error });
      throw error;
    }
  }

  // ============================================
  // SEARCH METHODS
  // ============================================

  /**
   * Search users by name prefix
   * Matches Flutter's UsersDataSource.searchUsers
   * Queries 'users' collection on firstLastNameLower or lastFirstNameLower
   * using range query for prefix matching
   *
   * @param keyword - Search keyword
   * @param cursor - Pagination cursor
   * @param limitCount - Results per page (default 6)
   * @param searchLastName - If true, search lastFirstNameLower field
   * @returns Paginated search results
   */
  async searchUsers(
    keyword: string,
    cursor?: PageCursor,
    limitCount: number = 6,
    searchLastName: boolean = false
  ): Promise<{
    items: JsonObject[];
    hasMore: boolean;
    cursor?: PageCursor;
  }> {
    try {
      const field = searchLastName ? 'lastFirstNameLower' : 'firstLastNameLower';
      const fieldValue = keyword.toLowerCase().replace(/\s/g, '');

      const strFrontCode = fieldValue.substring(0, fieldValue.length - 1);
      const strEndCode = fieldValue[fieldValue.length - 1];
      const searchLimit = strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

      const usersRef = collection(db, 'users');

      let q = query(
        usersRef,
        where(field, '>=', fieldValue),
        where(field, '<', searchLimit),
        orderBy(field),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((d) => d.data());

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined,
      };
    } catch (error) {
      appLogger.error('Error searching users', 'FirestoreService.searchUsers', { error: error as Error });
      throw error;
    }
  }

  /**
   * Fetch the latest users, paginated.
   * Matches Flutter's UsersDataSource.getUsers (users_data_source.dart:13-50):
   * queries the 'users' collection filtered by `removed == false`, ordered by
   * the chosen sort field descending, cursor-paginated.
   *
   * @param orderByField - Firestore field to order by (e.g. 'created_timestamp' or 'last_seen_timestamp')
   * @param limitCount - Results per page
   * @param cursor - Pagination cursor (opaque snapshot of last fetched item)
   * @returns Paginated user documents
   */
  async getLatestUsers(
    orderByField: string,
    limitCount: number,
    cursor?: PageCursor
  ): Promise<{
    items: JsonObject[];
    hasMore: boolean;
    cursor?: PageCursor;
  }> {
    try {
      // Admin gate: only admin (type 2) / super-admin (type 3) users may read
      // the latest-users list. Raw literals mirror the future Cloud Function,
      // which will not import the web UserType enum (data must not import domain).
      const currentFirebaseId = auth.currentUser?.uid;
      if (!currentFirebaseId) {
        return { items: [], hasMore: false };
      }

      const currentUser = await this.getUser(currentFirebaseId);
      const userType = currentUser?.type as number | undefined;
      if (userType !== 2 && userType !== 3) {
        return { items: [], hasMore: false };
      }

      const usersRef = collection(db, 'users');

      let q = query(
        usersRef,
        where('removed', '==', false),
        orderBy(orderByField, 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((d) => d.data());

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined,
      };
    } catch (error) {
      appLogger.error('Error fetching latest users', 'FirestoreService.getLatestUsers', { error: error as Error });
      // Flutter swallows the error and returns the empty (default) response.
      // Preserve the cursor so a retry does not refetch from the top.
      return { items: [], hasMore: false, cursor };
    }
  }

  /**
   * Search content tags by name prefix
   * Matches Flutter's ContentDataSource.getContentTags
   * Queries 'contenttags' collection on 'name' field
   *
   * @param prefix - Search prefix
   * @param cursor - Pagination cursor
   * @param limitCount - Results per page (default 8)
   * @returns Paginated search results
   */
  async searchContentTags(
    prefix: string,
    cursor?: PageCursor,
    limitCount: number = 8
  ): Promise<{
    items: JsonObject[];
    hasMore: boolean;
    cursor?: PageCursor;
  }> {
    try {
      const strFrontCode = prefix.substring(0, prefix.length - 1);
      const strEndCode = prefix[prefix.length - 1];
      const searchLimit = strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);

      const contentTagsRef = collection(db, 'contenttags');

      let q = query(
        contentTagsRef,
        where('name', '>=', prefix),
        where('name', '<', searchLimit),
        orderBy('name', 'desc'),
        limit(limitCount)
      );

      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }

      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((d) => d.data());

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined,
      };
    } catch (error) {
      appLogger.error('Error searching content tags', 'FirestoreService.searchContentTags', { error: error as Error });
      throw error;
    }
  }

  /**
   * Get content by hashtag or content tag
   * Matches Flutter's content_data_source.dart -> getContentByHashTag
   * Uses collectionGroup('media') to query across all users
   */
  async getContentByTag(
    tagName: string,
    isHashtag: boolean,
    cursor?: PageCursor,
    limitCount: number = 9
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    const mediaRef = collectionGroup(db, 'media');

    const tagField = isHashtag ? 'hashTags' : 'contentTags';

    let q = query(
      mediaRef,
      where(tagField, 'array-contains', tagName),
      where('isDraft', '==', false),
      where('isProcessing', '==', false),
      where('blocked', '==', false),
      orderBy('created_timestamp', 'desc'),
      limit(limitCount)
    );

    if (cursor) {
      q = query(q, startAfter(cursor as QueryDocumentSnapshot));
    }

    const snapshot = await getDocs(q);
    const items: JsonObject[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    return {
      items,
      hasMore: snapshot.docs.length === limitCount,
      cursor: snapshot.docs[snapshot.docs.length - 1],
    };
  }

  /**
   * Paginated fetch of comments via collectionGroup('comments').
   * Filters by mediaPath and parentId (null for root, string for replies).
   */
  async getComments(
    mediaPath: string,
    parentId: string | null,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const commentsRef = collectionGroup(db, 'comments');
      const constraints: any[] = [where('mediaPath', '==', mediaPath)];

      if (parentId === null) {
        constraints.push(where('parentId', '==', null));
      } else {
        constraints.push(where('parentId', '==', parentId));
      }

      constraints.push(orderBy('popularity', 'desc'));
      constraints.push(orderBy('createdTimestamp', 'desc'));

      let q = query(commentsRef, ...constraints);
      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }
      q = query(q, limit(limitCount));

      const snapshot = await getDocs(q);
      return {
        items: snapshot.docs.map(d => d.data()),
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching comments', 'FirestoreService.getComments', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Paginated fetch of gift/unlock transactions for a piece of content via
   * collectionGroup('transactions').
   * Flutter parity: ContentReactionsDataSource.getMediaGifts
   * (lib/data/data_sources/content_reactions_data_source.dart:57-89).
   * Filters by transactionType in [gift, unlockContent] and mediaInfo.path == mediaPath.
   */
  async getMediaGifts(
    mediaPath: string,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const transactionsRef = collectionGroup(db, 'transactions');
      const constraints: QueryConstraint[] = [
        where('transactionType', 'in', [AppTransactionType.gift, AppTransactionType.unlockContent]),
        where('mediaInfo.path', '==', mediaPath),
      ];

      let q = query(transactionsRef, ...constraints);
      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }
      q = query(q, limit(limitCount));

      const snapshot = await getDocs(q);
      return {
        items: snapshot.docs.map(d => d.data()),
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching media gifts', 'FirestoreService.getMediaGifts', { context: { mediaPath }, error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Paginated fetch of users who liked a given content via collectionGroup('likes').
   * Filters by media `path` and `liked == true`, ordered by `createdDate` desc.
   * Mirrors Flutter `UsersDataSource.getContentLikedUsers`
   * (lib/data/data_sources/users_data_source.dart:253-296). The `usersBlockedMe`
   * filtering is intentionally NOT applied here — it is done in the domain use case.
   */
  async getContentLikedUsers(
    mediaPath: string,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const likesRef = collectionGroup(db, 'likes');
      let q = query(
        likesRef,
        where('path', '==', mediaPath),
        where('liked', '==', true),
        orderBy('createdDate', 'desc'),
      );
      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }
      q = query(q, limit(limitCount));

      const snapshot = await getDocs(q);
      const items: JsonObject[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return {
        items,
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching content liked users', 'FirestoreService.getContentLikedUsers', {
        context: { mediaPath },
        error: error as Error,
      });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Fetch a single comment by id across all `comments` subcollections.
   */
  async getCommentById(id: string): Promise<JsonObject | null> {
    try {
      const q = query(
        collectionGroup(db, 'comments'),
        where('id', '==', id),
        orderBy('createdTimestamp', 'desc'),
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      return snapshot.docs[0].data();
    } catch (error) {
      appLogger.error('Error fetching comment by id', 'FirestoreService.getCommentById', { error: error as Error });
      return null;
    }
  }

  /**
   * Fetch multiple comments whose id is in the provided list.
   */
  async getCommentsByIds(ids: string[]): Promise<JsonObject[]> {
    if (ids.length === 0) return [];
    try {
      const q = query(
        collectionGroup(db, 'comments'),
        where('id', 'in', ids),
        orderBy('createdTimestamp', 'desc'),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => d.data());
    } catch (error) {
      appLogger.error('Error fetching comments by ids', 'FirestoreService.getCommentsByIds', { error: error as Error });
      return [];
    }
  }

  /**
   * Paginated fetch of a roadmap item's comments.
   *
   * Flutter parity: RoadmapDataSource.getComments
   * (lib/data/data_sources/roadmap_data_source.dart:249-295).
   *
   * Roadmap comments live at the fixed subcollection
   * `roadmap/{roadmapItemId}/comments` and are queried DIRECTLY — NOT via
   * `collectionGroup('comments')` (that is the content path). Filters by
   * `parentId` (null for root comments, string for a thread's replies) and is
   * ordered by `popularity desc, createdTimestamp desc`.
   */
  async getRoadmapComments(
    roadmapItemId: string,
    parentId: string | null,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const commentsRef = collection(db, 'roadmap', roadmapItemId, 'comments');
      const constraints: QueryConstraint[] = [];

      if (parentId === null) {
        constraints.push(where('parentId', '==', null));
      } else {
        constraints.push(where('parentId', '==', parentId));
      }

      constraints.push(orderBy('popularity', 'desc'));
      constraints.push(orderBy('createdTimestamp', 'desc'));

      let q = query(commentsRef, ...constraints);
      if (cursor) {
        q = query(q, startAfter(cursor as QueryDocumentSnapshot));
      }
      q = query(q, limit(limitCount));

      const snapshot = await getDocs(q);
      return {
        items: snapshot.docs.map(d => d.data()),
        hasMore: snapshot.docs.length === limitCount,
        cursor: snapshot.docs[snapshot.docs.length - 1],
      };
    } catch (error) {
      appLogger.error('Error fetching roadmap comments', 'FirestoreService.getRoadmapComments', {
        context: { roadmapItemId },
        error: error as Error,
      });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Fetch the roadmap comments referenced by a notification deep-link.
   *
   * Flutter parity: RoadmapDataSource.getNotificationComments
   * (lib/data/data_sources/roadmap_data_source.dart:219-247).
   *
   * Queries the fixed `roadmap/{roadmapItemId}/comments` subcollection for the
   * docs whose `id` is in the provided list, ordered by `createdTimestamp desc`.
   * The `ids` list is at most 3 (comment id + optional parentId + optional
   * replyId), so it never exceeds Firestore's `where in` cap of 10 — no
   * FIRESTORE_WHERE_IN_MAX_VALUES chunking is required.
   */
  async getRoadmapNotificationComments(
    roadmapItemId: string,
    ids: string[],
  ): Promise<JsonObject[]> {
    if (ids.length === 0) return [];
    try {
      const q = query(
        collection(db, 'roadmap', roadmapItemId, 'comments'),
        where('id', 'in', ids),
        orderBy('createdTimestamp', 'desc'),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => d.data());
    } catch (error) {
      appLogger.error('Error fetching roadmap notification comments', 'FirestoreService.getRoadmapNotificationComments', {
        context: { roadmapItemId },
        error: error as Error,
      });
      return [];
    }
  }

  /**
   * Add a roadmap comment via a Firestore transaction.
   *
   * Flutter parity: RoadmapDataSource.addComment
   * (lib/data/data_sources/roadmap_data_source.dart:297-417).
   *
   * Roadmap comments live at the fixed path `roadmap/{roadmapItemId}/comments/{commentId}`
   * (NOT the content `users/{author}/comments` path) and the roadmap item /
   * comment ids are always known, so the refs are built DIRECTLY — no
   * `findMediaRefByPath`/`findCommentRefById` collection-group lookup.
   *
   * Transaction order (must match Flutter :393-408):
   *   1. update roadmap item   { comments: increment(1) }
   *   2. set   comment doc     commentFirestoreMap (merge)
   *   3. set   notification    { stats, id, info } (merge) — when notification present
   *   4. set   parent comment  { replies: increment(1), popularity: increment(replyPop) } (merge) — when parentId
   *   5. set   replyToReply    same shape (merge) — when commentToReplyId
   *
   * Notification-id + stats branching (Flutter :324-373):
   *   - parentId present → id `"{parentId}reply"`, stats { commentReplies: +1 }
   *     and if commentToReplyId present → id `"{commentToReplyId}reply"` (still commentReplies)
   *   - else (root)      → id `"{roadmapItemId}comment"`, stats { contentComments: +1 }
   *
   * Returns `false` on any error (parity with Flutter's `bool success`).
   */
  async addRoadmapComment(params: {
    commentId: string;
    roadmapItemId: string;
    parentId: string | null;
    commentToReplyId: string | null;
    commentFirestoreMap: JsonObject;
    notification: { peerUserId: string; infoFirestoreMap: JsonObject } | null;
    commentReplyPopularity: number;
  }): Promise<boolean> {
    try {
      return await runTransaction(db, async (transaction) => {
        const roadmapItemRef = doc(db, 'roadmap', params.roadmapItemId);
        const commentRef = doc(collection(roadmapItemRef, 'comments'), params.commentId);

        let parentCommentRef: DocumentReference | null = null;
        let replyToReplyCommentRef: DocumentReference | null = null;
        let notificationId: string | null = null;
        let notificationStats: Record<string, unknown> | null = null;

        if (params.parentId) {
          parentCommentRef = doc(collection(roadmapItemRef, 'comments'), params.parentId);
          if (params.notification) {
            notificationId = `${params.parentId}reply`;
            notificationStats = { commentReplies: increment(1) };
          }
          if (params.commentToReplyId) {
            replyToReplyCommentRef = doc(
              collection(roadmapItemRef, 'comments'),
              params.commentToReplyId,
            );
            if (params.notification) {
              notificationId = `${params.commentToReplyId}reply`;
              notificationStats = { commentReplies: increment(1) };
            }
          }
        } else if (params.notification) {
          notificationId = `${params.roadmapItemId}comment`;
          notificationStats = { contentComments: increment(1) };
        }

        const repliesCountInfo = {
          replies: increment(1),
          popularity: increment(params.commentReplyPopularity),
        };

        transaction.update(roadmapItemRef, { comments: increment(1) });
        transaction.set(commentRef, params.commentFirestoreMap, { merge: true });

        if (params.notification && notificationId && notificationStats) {
          const notificationCommentRef = doc(
            db,
            'users',
            params.notification.peerUserId,
            'notifications',
            notificationId,
          );
          transaction.set(
            notificationCommentRef,
            {
              stats: notificationStats,
              id: notificationId,
              info: params.notification.infoFirestoreMap,
            },
            { merge: true },
          );
        }

        if (parentCommentRef) {
          transaction.set(parentCommentRef, repliesCountInfo, { merge: true });
          if (replyToReplyCommentRef) {
            transaction.set(replyToReplyCommentRef, repliesCountInfo, { merge: true });
          }
        }

        return true;
      });
    } catch (error) {
      appLogger.error('Error adding roadmap comment', 'FirestoreService.addRoadmapComment', {
        error: error as Error,
      });
      return false;
    }
  }

  /**
   * Like or unlike a roadmap comment via a Firestore transaction.
   *
   * Flutter parity: RoadmapDataSource.likeComment
   * (lib/data/data_sources/roadmap_data_source.dart:419-493).
   *
   * Refs are built DIRECTLY (the comment + roadmap item ids are known):
   *   - like  : users/{userId}/roadmap_comment_likes/{commentId}
   *             (NOTE: `roadmap_comment_likes`, NOT the content `comment_likes`)
   *   - comment: roadmap/{roadmapItemId}/comments/{commentId}
   *   - notification: users/{peerUserId}/notifications/{commentId}like
   *   - parent : roadmap/{parentCommentId}  ← verified Flutter behaviour: a
   *     TOP-LEVEL `roadmap` doc keyed by the parent comment id, NOT the
   *     comments subcollection. Ported as-is (Flutter :457-461). Only set when
   *     parentCommentId is present.
   *
   * Transaction order (must match Flutter :474-484):
   *   1. set    like doc     likeFirestoreMap (merge)
   *   2. update comment doc  { likes: increment(±1), popularity: increment(likePop) }
   *   3. set    notification { stats: { commentLikes: ±1 }, id, info } (merge)
   *   4. set    parent doc   { popularity: increment(likePop) } (merge) — when parentCommentId
   *
   * Popularity increment is `commentLikePopularity` for BOTH like and unlike —
   * Flutter does NOT negate it on unlike (only the `likes`/`commentLikes`
   * counts flip sign). Ported exactly.
   *
   * Guards on a present notification with peerUserId (the roadmap like always
   * carries a notification — Flutter takes a non-nullable `AppNotification`).
   * Returns `false` on any error (parity with Flutter's `bool success`).
   */
  async likeRoadmapComment(params: {
    commentId: string;
    userId: string;
    parentCommentId: string | null;
    liked: boolean;
    likeFirestoreMap: JsonObject;
    notification: { peerUserId: string; infoFirestoreMap: JsonObject } | null;
    roadmapItemId: string;
    commentLikePopularity: number;
  }): Promise<boolean> {
    try {
      return await runTransaction(db, async (transaction) => {
        if (!params.notification?.peerUserId) {
          throw new Error('likeRoadmapComment requires notification info with peerUserId');
        }

        const currentUserRef = doc(db, 'users', params.userId);
        const commentLikeRef = doc(
          collection(currentUserRef, 'roadmap_comment_likes'),
          params.commentId,
        );

        const roadmapItemRef = doc(db, 'roadmap', params.roadmapItemId);
        const commentRef = doc(collection(roadmapItemRef, 'comments'), params.commentId);

        const notificationId = `${params.commentId}like`;
        const notificationLikeRef = doc(
          db,
          'users',
          params.notification.peerUserId,
          'notifications',
          notificationId,
        );

        let parentCommentRef: DocumentReference | null = null;
        if (params.parentCommentId) {
          // Verified Flutter behaviour: top-level `roadmap` doc keyed by the
          // parent comment id (NOT roadmap/{itemId}/comments/{parentId}).
          parentCommentRef = doc(db, 'roadmap', params.parentCommentId);
        }

        const likesIncrement = increment(params.liked ? 1 : -1);
        const popularityIncrement = increment(params.commentLikePopularity);

        transaction.set(commentLikeRef, params.likeFirestoreMap, { merge: true });
        transaction.update(commentRef, {
          likes: likesIncrement,
          popularity: popularityIncrement,
        });
        transaction.set(
          notificationLikeRef,
          {
            stats: { commentLikes: likesIncrement },
            id: notificationId,
            info: params.notification.infoFirestoreMap,
          },
          { merge: true },
        );
        if (parentCommentRef) {
          transaction.set(
            parentCommentRef,
            { popularity: popularityIncrement },
            { merge: true },
          );
        }

        return true;
      });
    } catch (error) {
      appLogger.error('Error liking roadmap comment', 'FirestoreService.likeRoadmapComment', {
        error: error as Error,
      });
      return false;
    }
  }

  /**
   * Subscribe to live changes of a user media by id in the user's nested
   * subcollection. Mirrors Flutter's user_details_screen.dart:_listenToMediaLiveChanges
   * (lib/presentation/screens/user/user_details_screen.dart:309).
   *
   * Path: users/{userId}/media — filter: where('id', '==', mediaId).
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToUserMediaById(
    userId: string,
    mediaId: string,
    onDocChanges: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collection(db, 'users', userId, 'media'),
      where('id', '==', mediaId),
    );
    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: { id: c.doc.id, ...c.doc.data() },
        }));
        if (changes.length > 0) onDocChanges(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Subscribe to a single comment by id (collectionGroup) in real time.
   * Mirrors Flutter's _subscribeToContentReply
   * (lib/presentation/bottom_sheet/comments_bottom_sheet.dart:860-868).
   */
  subscribeToCommentById(
    id: string,
    onChange: (data: JsonObject | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collectionGroup(db, 'comments'),
      where('id', '==', id),
      orderBy('createdTimestamp', 'desc'),
      // `limit(1)` mirrors Flutter `comments_bottom_sheet.dart:866`; `data-layer.md` §Flutter parity
      // requires Firestore limits to match exactly.
      limit(1),
    );
    return onSnapshot(
      q,
      snapshot => {
        if (snapshot.empty) {
          onChange(null);
          return;
        }
        onChange(snapshot.docs[0].data());
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Subscribe to live changes of the current user's exclusive-content subscriptions.
   * Mirrors Flutter's ExclusiveContentService.listenToExclusiveContentChanges
   * (lib/services/exclusive_content_service.dart:13-62).
   *
   * Path: users/{currentUserFirebaseId}/subscriptions — filter: where('meSubscriber', '==', true).
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToExclusiveContentSubscriptionsLiveChanges(
    currentUserFirebaseId: string,
    onDocChanges: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collection(db, 'users', currentUserFirebaseId, 'subscriptions'),
      where('meSubscriber', '==', true),
    );
    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: c.doc.data(),
        }));
        if (changes.length > 0) onDocChanges(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Subscribe to live changes of the given user's premium packages.
   * Mirrors Flutter's PremiumPackagesService._initUserPremiumPackages
   * (lib/services/premium_packages_service.dart:13-58).
   *
   * Path: users/{userId}/premium_packages — no filter, no order.
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToUserPremiumPackagesLiveChanges(
    userId: string,
    onDocChanges: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collection(db, 'users', userId, 'premium_packages'),
    );
    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: c.doc.data(),
        }));
        if (changes.length > 0) onDocChanges(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Subscribe to live changes of the wallet document for the given user.
   * Mirrors Flutter's WalletService.listenToWalletChanges
   * (lib/services/wallet_service.dart:13-29).
   *
   * Path: wallet/{userId} — single document.
   * Emits `null` when the document is missing or has no data (mirrors
   * Flutter's `if (walletInfo != null)` guard which writes nothing in
   * that case); otherwise emits the raw document data.
   * Returns an Unsubscribe; callers are responsible for cleanup.
   */
  subscribeToWalletLiveChanges(
    userId: string,
    onChange: (data: JsonObject | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return onSnapshot(
      doc(db, 'wallet', userId),
      snapshot => {
        if (!snapshot.exists()) {
          onChange(null);
          return;
        }
        const data = snapshot.data();
        if (!data || Object.keys(data).length === 0) {
          onChange(null);
          return;
        }
        onChange(data);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Subscribe to live changes of the `user_device` document for the given user.
   * Mirrors Flutter's listener in
   * lib/services/app_authentication_service.dart:612-648 — same query shape
   * (`where('userId', '==', userId)`) so the security-rules surface matches.
   *
   * On every snapshot, emits the docChanges with their type ('added' |
   * 'modified' | 'removed') and document data. Callers (UserDeviceService)
   * are responsible for filtering by change type — Flutter only acts on
   * `modified`, but we emit all types so the service layer owns the parity
   * rule (and tests can cover it).
   */
  subscribeToUserDeviceChanges(
    userId: string,
    onDocChanges: (changes: Array<{ type: RemoteDocumentChangeType; data: JsonObject }>) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      collection(db, 'user_device'),
      where('userId', '==', userId),
    );
    return onSnapshot(
      q,
      snapshot => {
        const changes = snapshot.docChanges().map(c => ({
          type: c.type as RemoteDocumentChangeType,
          data: c.doc.data(),
        }));
        if (changes.length > 0) onDocChanges(changes);
      },
      error => onError?.(error as Error),
    );
  }

  /**
   * Find the most recent media document ref for a given path (collectionGroup).
   */
  async findMediaRefByPath(mediaPath: string): Promise<DocumentReference | null> {
    const q = query(
      collectionGroup(db, 'media'),
      where('path', '==', mediaPath),
      orderBy('created_timestamp', 'desc'),
      limit(1),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return snapshot.docs[0].ref;
  }

  /**
   * Find a comment document ref by id across all `comments` subcollections.
   */
  async findCommentRefById(id: string): Promise<DocumentReference | null> {
    const q = query(
      collectionGroup(db, 'comments'),
      where('id', '==', id),
      orderBy('createdTimestamp', 'desc'),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return snapshot.docs[0].ref;
  }

  /**
   * Find a comment document ref under a specific user's `comments` subcollection.
   */
  async findUserCommentRefById(userId: string, id: string): Promise<DocumentReference | null> {
    const q = query(
      collection(db, 'users', userId, 'comments'),
      where('id', '==', id),
      orderBy('createdTimestamp', 'desc'),
      limit(1),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return snapshot.docs[0].ref;
  }

  /**
   * Write (merge) a notification document under users/{userId}/notifications/{notificationId}.
   */
  async setUserNotification(
    userId: string,
    notificationId: string,
    infoFirestoreMap: JsonObject,
  ): Promise<void> {
    const notificationRef = doc(
      db,
      'users',
      userId,
      'notifications',
      notificationId,
    );
    await setDoc(
      notificationRef,
      { id: notificationId, info: infoFirestoreMap },
      { merge: true },
    );
  }

  /**
   * Mark a single notification document as read.
   *
   * Path: users/{ownerUserId}/notifications/{notificationId}
   * Update: { 'info.read': true }
   *
   * Mirrors Flutter `NotificationsDataSource.setNotificationRead`
   * (lib/data/data_sources/notifications_data_source.dart:5-12):
   *   FirebaseFirestore.instance
   *     .collection('users')
   *     .doc(notification.info.usersInfo.peerUserId)
   *     .collection('notifications')
   *     .doc(notification.id)
   *     .update({'info.read': true});
   *
   * The update key is the **dotted field path** `'info.read'` — a targeted
   * field update, NOT a merge-set of a nested `{ info: { read: true } }`
   * object. `ownerUserId` is the notification's `info.usersInfo.peerUserId`
   * (the recipient), the same field `getNotifications` filters on. The Flutter
   * `AppNotification` / `NotificationInfo` / `UsersInfo` models carry no
   * `@JsonKey` overrides (lib/data/models/notification_info.dart:30-35), so the
   * Dart property names ARE the Firestore fields.
   *
   * This method always issues the write — matching Flutter's data source. The
   * `if (!notification.info.read)` gate lives at the caller
   * (lib/services/push_navigation_service.dart:357-359); on web that is the
   * inbox feature hook's `markNotificationRead`, not this method and not the
   * page.
   *
   * Errors are logged and re-thrown so the domain layer can report failure
   * (Flutter's `await` propagates likewise).
   */
  async setNotificationRead(ownerUserId: string, notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(
        db,
        'users',
        ownerUserId,
        'notifications',
        notificationId,
      );
      await updateDoc(notificationRef, { 'info.read': true });
    } catch (error) {
      appLogger.error('Error setting notification read', 'FirestoreService.setNotificationRead', { error: error as Error });
      throw error;
    }
  }

  /**
   * Add a comment via a Firestore transaction (mirrors Flutter addComment).
   *   - increments media.comments by 1
   *   - sets users/{author}/comments/{commentId} (merge) with commentFirestoreMap
   *   - if notification & peerUserId: sets notification doc (merge) with stats
   *   - if parentId: parent comment replies++ and popularity+=replyPopularity
   *   - if commentToReplyId: replied-to comment replies++ popularity+=replyPopularity
   */
  async addComment(params: {
    commentId: string;
    mediaPath: string;
    parentId: string | null;
    commentToReplyId: string | null;
    authorUserId: string;
    commentFirestoreMap: JsonObject;
    notification: { peerUserId: string; infoFirestoreMap: JsonObject } | null;
    commentReplyPopularity: number;
  }): Promise<boolean> {
    try {
      return await runTransaction(db, async (transaction) => {
        const mediaRef = await this.findMediaRefByPath(params.mediaPath);
        if (!mediaRef) {
          throw new Error(`Media not found for path ${params.mediaPath}`);
        }

        const authorUserRef = doc(db, 'users', params.authorUserId);
        const userMediaCommentRef = doc(
          collection(authorUserRef, 'comments'),
          params.commentId,
        );

        let parentCommentRef: DocumentReference | null = null;
        let replyToReplyCommentRef: DocumentReference | null = null;
        let notificationId: string | null = null;

        if (params.parentId) {
          parentCommentRef = await this.findCommentRefById(params.parentId);
          if (!parentCommentRef) {
            throw new Error(`Parent comment not found: ${params.parentId}`);
          }
          notificationId = `${parentCommentRef.id}reply`;

          if (params.commentToReplyId) {
            const r2rRef = await this.findCommentRefById(params.commentToReplyId);
            if (r2rRef) {
              replyToReplyCommentRef = r2rRef;
              notificationId = `${replyToReplyCommentRef.id}reply`;
            }
          }
        } else {
          notificationId = `${mediaRef.id}comment`;
        }

        transaction.update(mediaRef, { comments: increment(1) });
        transaction.set(userMediaCommentRef, params.commentFirestoreMap, { merge: true });

        if (params.notification && notificationId && params.notification.peerUserId) {
          const notificationCommentRef = doc(
            db,
            'users',
            params.notification.peerUserId,
            'notifications',
            notificationId,
          );
          const stats: Record<string, any> = params.parentId
            ? { commentReplies: increment(1) }
            : { contentComments: increment(1) };
          transaction.set(
            notificationCommentRef,
            {
              stats,
              id: notificationId,
              info: params.notification.infoFirestoreMap,
            },
            { merge: true },
          );
        }

        if (parentCommentRef) {
          transaction.set(
            parentCommentRef,
            { replies: increment(1), popularity: increment(params.commentReplyPopularity) },
            { merge: true },
          );
          if (replyToReplyCommentRef) {
            transaction.set(
              replyToReplyCommentRef,
              { replies: increment(1), popularity: increment(params.commentReplyPopularity) },
              { merge: true },
            );
          }
        }

        return true;
      });
    } catch (error) {
      appLogger.error('Error adding comment', 'FirestoreService.addComment', { error: error as Error });
      return false;
    }
  }

  /**
   * Like or unlike a comment via transaction (mirrors Flutter likeComment).
   *   - sets users/{userId}/comment_likes/{commentId} (merge) with likeFirestoreMap
   *   - updates peer comment likes/popularity
   *   - if notification: sets peer notification (merge) with commentLikes stat
   *   - if parentCommentId: updates parent comment popularity
   */
  async likeComment(params: {
    commentId: string;
    userId: string;
    parentCommentId: string | null;
    liked: boolean;
    likeFirestoreMap: JsonObject;
    notification: { peerUserId: string; infoFirestoreMap: JsonObject } | null;
    commentLikePopularity: number;
  }): Promise<boolean> {
    try {
      return await runTransaction(db, async (transaction) => {
        if (!params.notification?.peerUserId) {
          throw new Error('likeComment requires notification info with peerUserId');
        }

        const currentUserRef = doc(db, 'users', params.userId);
        const commentLikeRef = doc(
          collection(currentUserRef, 'comment_likes'),
          params.commentId,
        );

        let parentCommentRef: DocumentReference | null = null;
        if (params.parentCommentId) {
          parentCommentRef = await this.findUserCommentRefById(
            params.userId,
            params.parentCommentId,
          );
        }

        const peerUserRef = doc(db, 'users', params.notification.peerUserId);
        const commentRef = doc(collection(peerUserRef, 'comments'), params.commentId);
        const notificationId = `${commentRef.id}like`;
        const notificationLikeRef = doc(
          collection(peerUserRef, 'notifications'),
          notificationId,
        );

        const likesIncrement = increment(params.liked ? 1 : -1);
        const popularityIncrement = increment(
          params.liked ? params.commentLikePopularity : -params.commentLikePopularity,
        );

        transaction.set(commentLikeRef, params.likeFirestoreMap, { merge: true });
        transaction.update(commentRef, {
          likes: likesIncrement,
          popularity: popularityIncrement,
        });
        transaction.set(
          notificationLikeRef,
          {
            stats: { commentLikes: likesIncrement },
            id: notificationId,
            info: params.notification.infoFirestoreMap,
          },
          { merge: true },
        );
        if (parentCommentRef) {
          transaction.set(
            parentCommentRef,
            { popularity: popularityIncrement },
            { merge: true },
          );
        }
        return true;
      });
    } catch (error) {
      appLogger.error('Error liking comment', 'FirestoreService.likeComment', { error: error as Error });
      return false;
    }
  }

  /**
   * Toggle a peer in the CURRENT user's `blockedConversations` array.
   *
   * Writes to:
   * - /users/{currentUserId} - the caller's OWN user doc (NOT the peer's),
   *   field `blockedConversations` (camelCase; the Dart `update` map key has
   *   no `@JsonKey` rename, so the literal Firestore field is `blockedConversations`).
   *
   * Uses `arrayUnion([peerId])` when `block` is true and `arrayRemove([peerId])`
   * when false.
   *
   * Matches Flutter: lib/data/data_sources/users_data_source.dart:340-362
   * (`UsersDataSource.setBlockConversation`). The Flutter null-firebase-user
   * guard (`return false`) lives at the domain-service layer on web
   * (`ChatService.setBlockConversation`), which resolves the uid before
   * delegating here.
   *
   * @param currentUserId - The caller's Firebase uid (the doc to update).
   * @param peerId - The peer's Firebase id to add/remove from the array.
   * @param block - `true` to block (arrayUnion), `false` to unblock (arrayRemove).
   * @returns `true` on success, `false` on any caught error (parity with
   *   Flutter's `bool success` pattern).
   */
  async setBlockConversation(currentUserId: string, peerId: string, block: boolean): Promise<boolean> {
    try {
      const userRef = doc(db, 'users', currentUserId);
      await updateDoc(userRef, {
        blockedConversations: block ? arrayUnion(peerId) : arrayRemove(peerId),
      });
      return true;
    } catch (error) {
      appLogger.error(
        'Error setting block conversation',
        'FirestoreService.setBlockConversation',
        { error: error as Error },
      );
      return false;
    }
  }

  /**
   * Toggle a peer in the CURRENT user's `mutedUsers` array.
   *
   * Writes to:
   * - /users/{currentUserId} - the caller's OWN user doc (NOT the peer's),
   *   field `mutedUsers` (camelCase; the Dart `mutedUsers` property has no
   *   `@JsonKey` rename at lib/data/models/user.dart:73, so the literal
   *   Firestore field is `mutedUsers`).
   *
   * Uses `arrayUnion([peerId])` when `mute` is true and `arrayRemove([peerId])`
   * when false.
   *
   * Matches Flutter: lib/data/data_sources/users_data_source.dart:364-386
   * (`UsersDataSource.setMuteUser`). The Flutter null-firebase-user guard
   * (`return false`) lives at the domain-service layer on web
   * (`ChatService.setMuteUser`), which resolves the uid before delegating here.
   *
   * @param currentUserId - The caller's Firebase uid (the doc to update).
   * @param peerId - The peer's Firebase id to add/remove from the array.
   * @param mute - `true` to mute (arrayUnion), `false` to unmute (arrayRemove).
   * @returns `true` on success, `false` on any caught error (parity with
   *   Flutter's `bool success` pattern).
   */
  async setMuteUser(currentUserId: string, peerId: string, mute: boolean): Promise<boolean> {
    try {
      const userRef = doc(db, 'users', currentUserId);
      await updateDoc(userRef, {
        mutedUsers: mute ? arrayUnion(peerId) : arrayRemove(peerId),
      });
      return true;
    } catch (error) {
      appLogger.error(
        'Error setting mute user',
        'FirestoreService.setMuteUser',
        { error: error as Error },
      );
      return false;
    }
  }
}

// Export singleton instance
export const firestoreService = FirestoreService.getInstance();